/**
 * O cliente do serviço `cadastro` — a ficha da Empresa, os Responsáveis pela empresa e as cinco
 * listas de domínio.
 *
 * O módulo espelha o serviço, um a um. **Nenhum tipo de contrato é digitado aqui** (AD-16):
 * todos são apelidos dos schemas gerados em `src/api/tipos-gerados/cadastro.ts`. Alterou o
 * contrato do serviço, rode `npm run gerar-tipos` — o CI reprova a divergência.
 *
 * As funções são chamadas autenticadas comuns: nenhuma passa `semRenovacao`, porque um acesso
 * expirado no meio do preenchimento de uma ficha deve se resolver sozinho, sem jogar a pessoa
 * de volta para a tela de login com o formulário perdido.
 *
 * **`Responsável` e `Responsável pela empresa` são dois conceitos**, e o módulo trata os dois:
 * o primeiro é gente da Orvalho — o dono da conta, trocado por `trocarResponsavel` —, o segundo
 * é a Pessoa de contato dentro da Empresa. A lista de quem pode ser o primeiro vem da
 * `identidade` (`useResponsaveis`), e não daqui: o dono de `usuario` é aquele serviço.
 *
 * **A listagem devolve um envelope, e não uma lista crua.** `GET /empresas` responde
 * `{itens, total}` (desde a 13.11b, um número só): o total é o subtítulo do cabeçalho da
 * tela, e ele tem de ser **pós-policy** — cada perfil enxerga uma base diferente, e um segundo endpoint
 * seria outra transação, com outra chance de os números discordarem da página. O envelope não
 * traz `responsavel_nome` nem `setor_rotulo`: os dois são resolvidos aqui no cliente, por
 * `useResponsaveis()` e `useOpcoesDeDominio()`, que são duas consultas para a tela inteira em
 * vez de duas por linha.
 *
 * O que **não** existe aqui é o resto do épico: orfanar uma ficha (2.7 — é ato de sistema, e
 * não tem porta de usuário) e excluir ou desvincular uma Pessoa (épico 8, FR-40). Não há função
 * porque não há rota do outro lado — e é assim que o recorte da história aparece no cliente.
 */

import { useMemo } from 'react'

import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { criarApiDeCamposPersonalizados } from '@/api/campos-personalizados'
import { chamar, chamarComStatus } from '@/api/cliente'
import { ErroDaApi } from '@/api/erros'
import type { components } from '@/api/tipos-gerados/cadastro'

import { nomeExibidoDaEmpresa } from './nome-da-empresa'

/** Os schemas do `openapi.json` do `cadastro`, gerados por `npm run gerar-tipos` (AD-16). */
type Esquemas = components['schemas']

/** A ficha da Empresa, como as três rotas a devolvem. */
export type Empresa = Esquemas['RespostaDeEmpresa']

/**
 * O desfecho da consulta de CNPJ/CPF contra o ERP (FR-24, história 5.1).
 *
 * São três, e a terceira é a que faz a consulta valer a pena: `inedito` (o Carga Máquina não
 * conhece o documento, e o formulário segue), `existente` (já há ficha vinculada àquela Pessoa, e a
 * tela **abre** a que existe) e `criada` (a ficha nasceu já cadastrada no Carga Máquina, vinculada ao
 * `erp_pessoa_id`).
 *
 * **Não há um quarto valor para "já existe uma ficha no CRM com este documento".** A consulta cobre
 * apenas o Carga Máquina e não varre a base do CRM (FR-24, decisão do PRD): Empresa repetida continua
 * aceita e é resolvida por limpeza da equipe.
 */
export type SituacaoDoDocumento = Esquemas['SituacaoDoDocumento']

/**
 * O que a consulta responde: a situação e, quando há uma, a ficha a abrir.
 *
 * `empresa_id` é nulo **só** em `inedito`. Nos outros dois há sempre uma ficha, e é o `id` dela que
 * a tela usa para navegar — a ficha em si é lida por `GET /empresas/{id}`, como em qualquer outra
 * abertura.
 */
export type ConsultaDeDocumento = Esquemas['RespostaDaConsultaDeDocumento']

/** O que o formulário envia para criar. `responsavel_id` é obrigatório (FR-26). */
export type NovaEmpresa = Esquemas['CorpoDeNovaEmpresa']

/**
 * Uma linha de Pessoa do "+ Empresa" (13.14a, DW-379): os quatro campos e a resposta da pergunta da
 * adoção — **sem** `principal`. Vai em `NovaEmpresa.pessoas`, gravada na mesma transação da Empresa.
 */
export type PessoaDaFichaNovaNoCorpo = Esquemas['CorpoDaPessoaDaFichaNova']

/**
 * A linha como a tela a monta: a resposta da adoção é opcional (o serviço assume `perguntar`). O tipo
 * gerado a declara obrigatória porque tem padrão — mandar `perguntar` em toda linha seria ruído.
 */
export type LinhaDaFichaNova = Omit<PessoaDaFichaNovaNoCorpo, 'adocao'> &
  Partial<Pick<PessoaDaFichaNovaNoCorpo, 'adocao'>>

/**
 * A linha da ficha nova que leva uma Pessoa que **já existe** (13.19c1): só o `pessoa_id` da
 * escolhida na sugestão. O serviço a liga à Empresa nova sem criar outra.
 */
export type PessoaExistenteNoCorpo = Esquemas['CorpoDaPessoaExistenteDaFichaNova']

/** A ficha nova como a tela a monta: as Pessoas são `LinhaDaFichaNova` ou a existente (13.19c1). */
export type PedidoDeEmpresa = Omit<NovaEmpresa, 'pessoas'> & {
  pessoas?: (LinhaDaFichaNova | PessoaExistenteNoCorpo)[]
}

/** A ficha criada e as Pessoas que nasceram com ela, na ordem enviada (13.14a). */
export type EmpresaCriada = Esquemas['RespostaDaEmpresaCriada']

/**
 * As Pessoas que nasceram com a ficha — `[]` também quando a resposta não as traz (a imagem anterior
 * à 13.14a, na volta de um deploy).
 */
export function pessoasDaCriada(criada: EmpresaCriada): EmpresaCriada['pessoas'] {
  const pessoas: unknown = (criada as Partial<EmpresaCriada>).pessoas
  return Array.isArray(pessoas) ? (pessoas as EmpresaCriada['pessoas']) : []
}

/** A resposta à pergunta da adoção da Pessoa solta de outro nome (13.14a, DW-388). */
export type Adocao = Esquemas['Adocao']

/** Uma linha recusada do `409` `pessoas_com_problema` (13.14a): `indice` 0-based na lista enviada. */
export type ProblemaDaPessoa = Esquemas['ProblemaDaPessoaNoEnvelope']

/** A solta da pergunta da adoção: o `id` que o "sim" devolve e o nome. */
export type ContatoPerguntado = Esquemas['ContatoNoEnvelope']

/** Os códigos das recusas novas da 13.14a — a tela decide o desenho por eles; a mensagem é do servidor. */
export const CODIGO_DAS_PESSOAS_COM_PROBLEMA = 'pessoas_com_problema'
export const CODIGO_DO_CONTATO_COM_OUTRO_NOME = 'contato_com_outro_nome'
export const CODIGO_DA_PESSOA_JA_CADASTRADA = 'pessoa_ja_cadastrada'
export const CODIGO_DO_TELEFONE_REPETIDO_NA_FICHA = 'telefone_repetido_na_ficha'

/** As linhas recusadas do `409` do "+ Empresa", ou `[]` quando a falha é outra. */
export function problemasDasPessoas(falha: unknown): ProblemaDaPessoa[] {
  if (!(falha instanceof ErroDaApi) || falha.codigo !== CODIGO_DAS_PESSOAS_COM_PROBLEMA) {
    return []
  }
  const linhas = falha.extras.pessoas
  if (!Array.isArray(linhas)) {
    return []
  }
  return linhas.filter(
    (linha): linha is ProblemaDaPessoa =>
      typeof linha === 'object' &&
      linha !== null &&
      typeof (linha as { indice?: unknown }).indice === 'number' &&
      typeof (linha as { mensagem?: unknown }).mensagem === 'string',
  )
}

/** A solta perguntada no `409` `contato_com_outro_nome`, ou `undefined` quando a falha é outra. */
export function contatoDaPergunta(falha: unknown): ContatoPerguntado | undefined {
  if (!(falha instanceof ErroDaApi) || falha.codigo !== CODIGO_DO_CONTATO_COM_OUTRO_NOME) {
    return undefined
  }
  const contato = falha.extras.contato as Partial<ContatoPerguntado> | undefined
  if (typeof contato?.id !== 'string' || typeof contato.nome !== 'string') {
    return undefined
  }
  return { id: contato.id, nome: contato.nome }
}

/*
 * **A porta enxuta (`POST /empresas/enxuta`) não tem mais chamador no SPA** (13.1): o "Novo
 * negócio" escolhe uma Empresa sugerida ou a cria pela ficha completa, que desde o FR-24 emendado
 * aceita a Empresa sem CNPJ. A rota fica no serviço (retirá-la é *Ask First*, com DW própria).
 */

/**
 * O que o formulário envia para editar: a ficha inteira, **sem** `responsavel_id`.
 *
 * O corpo é completo e não um delta — o que não vier volta ao padrão. Último a gravar vence:
 * duas pessoas com a mesma ficha aberta, a segunda a salvar desfaz a alteração da primeira,
 * sem erro e sem aviso. É consequência do corpo completo, e não há versão otimista no
 * contrato.
 */
export type EdicaoDeEmpresa = Esquemas['CorpoDeEdicaoDeEmpresa']

/** Ouro, Prata ou Bronze (FR-81) — a faixa escolhida à mão; substitui as estrelas (11.7). */
export type Faixa = Esquemas['Faixa']

/** O endereço como a ficha o devolve: sete partes, CEP só com dígitos e UF maiúscula (11.7). */
export type Endereco = Esquemas['RespostaDeEndereco']

/** O endereço como o corpo o leva — todas as partes opcionais; o serviço normaliza. */
export type CorpoDeEndereco = Esquemas['CorpoDeEndereco']

/** Um endereço de entrega da Empresa, com rótulo (11.7). */
export type EnderecoDeEntrega = Esquemas['RespostaDeEnderecoDeEntrega']

/** O corpo de um endereço de entrega novo. */
export type NovoEnderecoDeEntrega = Esquemas['CorpoDeEnderecoDeEntrega']

/** O desfecho da consulta de CNPJ na BrasilAPI, pelo `cadastro` (AD-28). */
export type ConsultaDeCnpj = Esquemas['RespostaDaConsultaDeCnpj']

/** O desfecho da consulta de CEP no ViaCEP, pelo `cadastro` (AD-28). */
export type ConsultaDeCep = Esquemas['RespostaDaConsultaDeCep']

/** Por que a consulta pública não trouxe dado: `nao_encontrado` ou `indisponivel`. */
export type MotivoDaConsulta = Esquemas['MotivoDaConsulta']

/**
 * O que a seção de Responsável envia: para quem a ficha vai, e nada mais.
 *
 * O campo é obrigatório e **não anulável**: a troca entrega a alguém. Orfanar é ato do
 * desligamento (2.7), em contexto de sistema, e não tem porta de usuário (FR-33, FR-34).
 */
export type TrocaDeResponsavel = Esquemas['CorpoDeResponsavel']

/** Uma opção de lista de domínio. O rótulo vem pronto — o SPA não guarda cópia do catálogo. */
export type OpcaoDeDominio = Esquemas['RespostaDeOpcao']

/** As quatro listas do `cadastro` (11.3), sempre as quatro. Lista vazia é `[]`, nunca ausente. */
export type OpcoesDeDominio = Esquemas['RespostaDeOpcoesDeDominio']

/**
 * As quatro listas vazias — o que a tela desenha antes de a resposta chegar, e o que os
 * testes usam como base.
 *
 * A anotação `OpcoesDeDominio` é o guarda de exaustividade: ela exige **todas** as chaves do
 * contrato e recusa qualquer uma que não seja dele, então lista nova no backend, ou lista
 * renomeada, quebra o build aqui — em vez de o seletor sumir da tela em silêncio.
 */
export const OPCOES_VAZIAS: OpcoesDeDominio = {
  categoria: [],
  setor: [],
  origem_do_cliente: [],
  motivo_de_perda: [],
}

/** Chave de cache da ficha; a tela de listagem (2.6) a compartilha. */
export const chaveDaEmpresa = (id: string) => ['cadastro', 'empresa', id] as const

/**
 * Uma linha da listagem: **seis** campos, e a magreza é do serviço (AD-3).
 *
 * Não tem o nome do Responsável nem o rótulo do Setor — só os `id`. Quem os resolve é a tela,
 * a partir de caches que ela já paga: `responsavel_id` aponta para `usuario`, que é de outro
 * serviço e de outro database (AD-21/AD-22), e o rótulo do Setor o SPA já tem inteiro.
 */
export type EmpresaNaLista = Esquemas['RespostaDeEmpresaNaLista']

/** A página **e** as duas contagens do mesmo filtro, como `GET /empresas` as devolve. */
export type PaginaDeEmpresas = Esquemas['RespostaDaListaDeEmpresas']

/**
 * O teto do termo de busca. **Espelha o serviço, e ele decide** — é o mesmo padrão de
 * `DIGITOS_DO_CNPJ` em `FormularioDeEmpresa.tsx`.
 *
 * O número existe aqui para que o campo o imponha por `maxLength` e a rota o corte no
 * `validateSearch`: sem isso, colar 101 caracteres troca a lista por um `422` cru do FastAPI,
 * que é o serviço explicando um limite que a tela nunca mostrou. O contrato gerado não carrega
 * `maxLength` de parâmetro de query, então este é o único jeito de o SPA conhecê-lo.
 */
export const TAMANHO_DA_BUSCA = 100

/**
 * O teto da página. **Espelha o serviço** (`PAGINA_MAXIMA`), pelo mesmo motivo de
 * `TAMANHO_DA_BUSCA`: o contrato gerado não traz a faixa de parâmetro de query.
 *
 * O teto existe do lado de lá porque um inteiro absurdo estoura o `bigint` do `OFFSET` e vira
 * `500` em vez de `422`. Aqui ele serve para a URL: `/empresas?pagina=200000` — um link antigo,
 * uma barra de endereço editada — cai na primeira página em vez de trocar a lista por um erro
 * de validação do serviço, que é justamente o que `validateSearch` existe para evitar.
 */
export const PAGINA_MAXIMA = 100_000

/**
 * O teto de itens por pedido. **Espelha o serviço** (`LIMITE_MAXIMO`), pelo mesmo motivo dos dois
 * acima: o contrato gerado não traz a faixa de parâmetro de query.
 *
 * Ele vale para **dois** parâmetros do mesmo endpoint, e é o mesmo número de propósito: o
 * `limite` da página e o tamanho do lote de `ids` (3.8). É isso que faz o lote e a página caberem
 * no mesmo orçamento de consulta — e é por ele que `useNomesDeEmpresas` quebra um quadro grande
 * em vários pedidos em vez de tomar um `422`.
 */
export const LIMITE_MAXIMO = 200

/** O que a tela de listagem pede: o termo, o lote de `id`, a página e o tamanho dela. */
export interface ParametrosDaListaDeEmpresas {
  /** Casa por razão social (sem diferenciar maiúsculas) ou pelos dígitos do documento. */
  busca?: string
  /**
   * O lote de `id` a resolver — o quadro do Funil (3.8) é quem o usa.
   *
   * **Ausente é "sem filtro"; lista vazia é "pedi zero fichas"**, e os dois não são a mesma
   * coisa: a listagem da 2.6 nunca manda o campo, e mandar uma lista vazia por engano devolveria
   * página vazia sobre uma base cheia. Por isso o lote vazio nem chega a virar URL — quem o pede
   * não tem o que perguntar.
   *
   * O teto é `LIMITE_MAXIMO` (200) ids; acima disso o serviço responde `422`. Quem precisa de
   * mais quebra em lotes, e é o que `useNomesDeEmpresas` faz.
   */
  ids?: readonly string[]
  /** De 1 a `PAGINA_MAXIMA`; fora dessa faixa é `422` do validador do serviço. */
  pagina?: number
  /** De 1 a `LIMITE_MAXIMO` (200) no serviço; a tela usa o padrão dele quando não pede outro. */
  limite?: number
  /** As fichas com esta etiqueta da equipe (13.6) — só as que quem olha enxerga. */
  etiquetaId?: string
  /**
   * A ordem (13.14a, DW-373): `nome` (o padrão, a tela Empresas) ou `semelhanca` — o campo de
   * Empresa com sugestão: o nome igual, o começo, a palavra inteira, e o resto. Só a ordem muda.
   */
  ordem?: 'nome' | 'semelhanca'
}

/**
 * O que vai para a chave de cache e para a URL — as chaves, sempre na mesma ordem.
 *
 * `{busca: ''}` e `{}` são a mesma consulta para o serviço (termo vazio não é filtro), e sem a
 * normalização seriam duas entradas de cache com o mesmo conteúdo: a segunda pagaria uma ida à
 * rede para receber o que a primeira já tinha.
 */
function normalizarParametros({
  busca,
  ids,
  pagina,
  limite,
  etiquetaId,
  ordem,
}: ParametrosDaListaDeEmpresas) {
  const termo = busca?.trim()
  return {
    busca: termo === undefined || termo === '' ? undefined : termo,
    /*
     * O lote entra na chave como texto ordenado, e não como array: o react-query compara a chave
     * por estrutura, e ali a ordem importa — para o serviço, não. Ordenar antes faz duas listas
     * com o mesmo conteúdo serem a mesma entrada de cache, que é o que elas são.
     *
     * **A lista vazia é preservada, e vira `''` — não `undefined`.** Os três estados do serviço
     * são "sem filtro" (ausente), "pedi zero fichas" (vazio) e "pedi estas" (com ids), e é para
     * impedir o primeiro acidente que `montar_filtro_dos_ids` devolve `false()` no segundo.
     * Colapsar vazio em ausente aqui desfaria essa guarda do lado do cliente: `useEmpresas({ids:
     * []})` devolveria a **base inteira** a quem pediu ficha nenhuma. `''` é distinguível de
     * `undefined` na chave de cache e em `listarEmpresas`, e é o que mantém os três separados.
     */
    ids: ids === undefined ? undefined : [...ids].sort().join(','),
    pagina,
    limite,
    // Na chave de cache (13.6): sem ela, a página filtrada e a sem filtro dividiriam uma entrada.
    etiquetaId: etiquetaId === undefined || etiquetaId === '' ? undefined : etiquetaId,
    // `nome` é o padrão do serviço: na chave e na URL, ausente e `nome` são a mesma consulta.
    ordem: ordem === 'semelhanca' ? ordem : undefined,
  }
}

/**
 * Chave de cache de **uma página**: o termo, a página e o limite entram nela.
 *
 * Sem os parâmetros na chave, digitar na busca reescreveria a mesma entrada, e a página 2 de
 * "orvalho" apareceria como se fosse a página 1 da base inteira. Com eles, voltar a uma busca
 * já feita é instantâneo — e é isso que faz o `placeholderData` de `useEmpresas` valer a pena.
 */
export const chaveDasEmpresas = (parametros: ParametrosDaListaDeEmpresas = {}) =>
  [...CHAVE_DA_LISTA_DE_EMPRESAS, normalizarParametros(parametros)] as const

/**
 * Prefixo comum de **todas** as páginas da listagem — é por ele que a invalidação as alcança.
 *
 * Cada página e cada termo têm a sua entrada de cache, e uma escrita na ficha muda o que
 * qualquer uma delas mostraria: `useInvalidarAsEmpresas` invalida o prefixo, e não uma chave,
 * porque não há como saber em qual página a Empresa alterada caiu — nem se ela ainda cai na
 * mesma depois da alteração (mudar a razão social a move de lugar no alfabeto).
 */
export const CHAVE_DA_LISTA_DE_EMPRESAS = ['cadastro', 'empresas'] as const

/** Chave de cache do vocabulário: uma só, porque a resposta traz as cinco listas juntas. */
export const CHAVE_DAS_OPCOES = ['cadastro', 'opcoes-de-dominio'] as const

/**
 * Lê a ficha (`200`).
 *
 * `404` quando a Empresa não existe **ou** quando quem pediu não a vê (FR-30, "é responsável, vê")
 * — os dois casos são a mesma resposta, de propósito: um `403` diria que há uma ficha ali, e a
 * visibilidade viraria um jogo de adivinhar `id`. Mostre a mensagem como ela veio.
 */
export function lerEmpresa(id: string): Promise<Empresa> {
  return chamar<Empresa>('cadastro', `/empresas/${encodeURIComponent(id)}`)
}

/**
 * Lê uma página da base (`200`), com as duas contagens do mesmo filtro.
 *
 * **Não há `403` aqui, e a ausência é a regra.** "Ver a base" não é uma linha do FR-61-A: quem
 * recorta é a RLS do serviço, linha a linha. Um Vendedor recebe `200` com a página dele — a
 * carteira dele, mais o que a PA-1, a PA-3 e o Grupo lhe dão —, e a Empresa de outro Responsável
 * simplesmente não está lá, nem na página nem no total (FR-30). Base vazia para quem pediu é `200` com
 * `itens: []` e o `total` em zero, nunca `404`.
 *
 * `422` quando `limite` sai de `[1, 200]`, `pagina` é menor que 1 ou `busca` passa de 100
 * caracteres — a tela não oferece nenhum dos três, e o `422` é a rede de quem chamar por fora.
 *
 * Parâmetro ausente **não** entra na URL: `busca=` é um termo vazio, que o serviço já trata
 * como ausente, mas produziria uma URL diferente para a mesma consulta.
 */
export function listarEmpresas(
  parametros: ParametrosDaListaDeEmpresas = {},
): Promise<PaginaDeEmpresas> {
  const { busca, ids, pagina, limite, etiquetaId, ordem } = normalizarParametros(parametros)
  /*
   * **Lote vazio não vai à rede**, e a resposta é a página vazia que o caso de uso daria.
   *
   * Pela URL o estado não é representável: `?ids=` sem valor é `422` (string vazia não é uuid) e
   * omitir o parâmetro quer dizer "sem filtro" — os dois estão errados aqui. O serviço tem o ramo
   * certo (`ids=[]` → página vazia), mas ele só é alcançável em Python. Cortar aqui devolve a
   * resposta correta e economiza a requisição: quem pede zero fichas já sabe que recebe zero.
   */
  if (ids === '') {
    return Promise.resolve({ itens: [], total: 0 })
  }
  const consulta = new URLSearchParams()
  if (busca !== undefined) {
    consulta.set('busca', busca)
  }
  if (ids !== undefined) {
    // Um par `ids=` por valor, que é o formato que o FastAPI lê para uma lista de uuid. Um único
    // `ids=a,b` chegaria como um uuid malformado e viraria `422`.
    for (const id of ids.split(',')) {
      consulta.append('ids', id)
    }
  }
  if (pagina !== undefined) {
    consulta.set('pagina', String(pagina))
  }
  if (limite !== undefined) {
    consulta.set('limite', String(limite))
  }
  if (etiquetaId !== undefined) {
    consulta.set('etiqueta_id', etiquetaId)
  }
  if (ordem !== undefined) {
    consulta.set('ordem', ordem)
  }
  const parametrosNaUrl = consulta.toString()
  return chamar<PaginaDeEmpresas>(
    'cadastro',
    parametrosNaUrl === '' ? '/empresas' : `/empresas?${parametrosNaUrl}`,
  )
}

/**
 * Cria a ficha (`201`). Exige a permissão `criar_empresa`.
 *
 * A Empresa nasce sem vínculo com o Carga Máquina, com a faixa e o endereço enviados (desde a 13.11a,
 * para outra pessoa só pelo escopo `todas`: os demais recebem `403`). Recusas em PT-BR, como
 * `ErroDaApi`: `400` documento fora do formato (a mensagem **nomeia** o formato) ou opção de
 * lista inexistente, `422` campo obrigatório ausente, faixa fora do enum ou CEP/UF fora do
 * formato (a mensagem **nomeia** o formato), `403` sem a
 * permissão, `401` sem sessão.
 *
 * Os campos vão **um a um**, e não o objeto que chegou: o corpo do serviço é fechado
 * (`extra="forbid"`), então um objeto de formulário com um campo a mais viraria um `422`
 * genérico que não diz qual campo sobrou — e a tipagem estrutural do TypeScript não pega isso
 * quando o objeto vem de uma variável.
 */
export function criarEmpresa(ficha: PedidoDeEmpresa): Promise<EmpresaCriada> {
  return chamar<EmpresaCriada>('cadastro', '/empresas', {
    method: 'POST',
    corpo: corpoDeCriacao(ficha),
  })
}

/**
 * Confere o CNPJ/CPF no ERP antes de cadastrar (`200`, ou `201` quando a ficha nasceu).
 *
 * São três desfechos, e o status separa "nada mudou" de "criei um recurso":
 *
 * - `200 {"situacao": "inedito", "empresa_id": null}` — o Carga Máquina não conhece o documento, e
 *   o resto do formulário segue. A ficha que nascer dali vai ao Carga Máquina no primeiro orçamento.
 * - `200 {"situacao": "existente", "empresa_id": "<uuid>"}` — já há ficha vinculada àquela Pessoa.
 *   **Nada é criado**, e a tela abre a ficha que existe: é este o caminho que impede a segunda
 *   ficha da Empresa que já comprou.
 * - `201 {"situacao": "criada", "empresa_id": "<uuid>"}` — uma ficha nasce, já vinculada ao
 *   Carga Máquina, com o Responsável sendo quem consultou.
 *
 * Exige `criar_empresa`: a rota **cria ficha**, e quem não pode cadastrar Empresa não pode fazê-la
 * nascer por aqui. `403` sem a permissão, `401` sem sessão.
 *
 * `400` quando o documento não tem o formato aceito, com a mensagem que o **nomeia** — e o ERP não
 * é chamado. `503` com a mensagem literal do FR-47 quando o Carga Máquina não responde: mostre-a
 * verbatim, e mantenha o formulário preenchível.
 *
 * O campo vai **um a um**, e não o objeto que chegou, pela mesma razão de `criarEmpresa`: o corpo do
 * serviço é fechado (`extra="forbid"`), e a tipagem estrutural do TypeScript não pega um campo a
 * mais quando o objeto vem de uma variável.
 */
export function consultarDocumento(
  documento: string,
  nomeFantasia?: string,
): Promise<ConsultaDeDocumento> {
  /*
   * 13.14a (DW-421): o nome fantasia que a pessoa já digitou vai junto, e a ficha que a consulta
   * criar (`criada`) nasce com ele. Só quando há um: a imagem anterior recusaria o campo (`422`).
   */
  const nome = nomeFantasia?.trim()
  return chamar<ConsultaDeDocumento>('cadastro', '/empresas/consulta-de-documento', {
    method: 'POST',
    corpo: (nome === undefined || nome === ''
      ? { documento }
      : { documento, nome_fantasia: nome }) satisfies Esquemas['CorpoDaConsultaDeDocumento'],
  })
}

/**
 * Grava a ficha inteira (`200`). Exige a permissão `editar_empresa`.
 *
 * **Reenviar os mesmos valores não publica evento nem deixa registro na trilha** — o serviço
 * compara antes de gravar. A resposta é `200` do mesmo jeito, com a ficha como ela está.
 *
 * `responsavel_id` não entra: trocar o Responsável é ato registrado (FR-32) e chega na 2.5.
 * Enviá-lo aqui é `422`, e não um campo ignorado em silêncio.
 */
export function editarEmpresa(id: string, ficha: EdicaoDeEmpresa): Promise<Empresa> {
  return chamar<Empresa>('cadastro', `/empresas/${encodeURIComponent(id)}`, {
    method: 'PUT',
    corpo: corpoDeEdicao(ficha),
  })
}

/**
 * Entrega a ficha a outro Responsável (`200`). Exige a permissão `trocar_responsavel`.
 *
 * É rota própria, e não um campo a mais no corpo da ficha, porque o FR-32 diz duas coisas na
 * mesma frase: qualquer usuário pode trocar o Responsável, e **toda** troca é registrada com
 * autor, data e hora. Um `responsavel_id` opcional em `editarEmpresa` faria a entrega acontecer
 * sem trilha própria, escondida no meio de uma edição de ficha — e enviá-lo lá é `422`.
 *
 * A permissão é **concedida aos quatro perfis** no FR-61-A. O `403`
 * só aparece quando um Admin ajustou a permissão daquele usuário (FR-61-B), e a mensagem do
 * serviço vai à tela verbatim.
 *
 * A resposta é a **ficha inteira**, e não só o campo, e é por isso que ela cabe em
 * `chaveDaEmpresa(id)`. **Reenviar o Responsável que já está lá não publica evento nem deixa
 * registro na trilha** — o serviço compara antes de gravar, e a resposta é `200` do mesmo
 * jeito. `404` quando a Empresa não existe **ou** quem pediu não a vê (FR-30).
 *
 * O corpo é montado aqui como literal de um campo só, conferido por `satisfies
 * TrocaDeResponsavel` contra o schema gerado: é o `satisfies` — e não a boa vontade de quem
 * chama — que garante que nada além de `responsavel_id` atravesse o `extra="forbid"` do
 * serviço.
 */
export function trocarResponsavel(id: string, responsavelId: string): Promise<Empresa> {
  return chamar<Empresa>('cadastro', `/empresas/${encodeURIComponent(id)}/responsavel`, {
    method: 'PUT',
    corpo: { responsavel_id: responsavelId } satisfies TrocaDeResponsavel,
  })
}

/**
 * Reenvia a Empresa ao ERP (`200`), a partir da ficha que está pendente de sincronização (5.5).
 *
 * Ela **não** escreve no Carga Máquina: o serviço republica, no outbox, o evento que faz o
 * `integracao-erp` escrever — e a escrita acontece no trabalhador, fora do ciclo desta
 * requisição. Por isso a resposta é imediata mesmo com o ERP fora do ar, e por isso o selo pode
 * voltar na passada seguinte do trabalhador se a nova tentativa falhar de novo.
 *
 * **Sem corpo**, e a ausência é o contrato: o ato não tem argumento nenhum — o que se pede é "de
 * novo", e o `id` do caminho já diz sobre o quê.
 *
 * A resposta é a **ficha inteira**, e é por isso que ela cabe em `chaveDaEmpresa(id)`.
 *
 * `409` quando a ficha não está mais pendente — normalmente porque o trabalhador venceu a corrida
 * com o clique e a sincronização já deu certo. `403` para quem não tem `editar_empresa`; `404`
 * quando a Empresa não existe **ou** quem pediu não a vê (FR-30). Mostre a
 * mensagem do serviço como ela veio.
 */
export function reenviarSincronizacao(id: string): Promise<Empresa> {
  return chamar<Empresa>('cadastro', `/empresas/${encodeURIComponent(id)}/sincronizacao`, {
    method: 'POST',
  })
}

/**
 * Consulta o CNPJ na BrasilAPI **pelo `cadastro`** (AD-28) — nunca do navegador.
 *
 * `200` sempre que o formato é válido: `encontrado: false` com `motivo` (`nao_encontrado`,
 * `indisponivel`) é resposta, e não falha — a tela pinta o erro no campo e o salvar segue liberado.
 * `422` com 13 dígitos ou CPF (o provedor nem é chamado): a tela só consulta CNPJ de 14 dígitos.
 */
export function consultarCnpj(cnpj: string): Promise<ConsultaDeCnpj> {
  return chamar<ConsultaDeCnpj>('cadastro', `/consultas/cnpj/${encodeURIComponent(cnpj)}`)
}

/** Consulta o CEP no ViaCEP **pelo `cadastro`** (AD-28). Mesmo contrato de `consultarCnpj`. */
export function consultarCep(cep: string): Promise<ConsultaDeCep> {
  return chamar<ConsultaDeCep>('cadastro', `/consultas/cep/${encodeURIComponent(cep)}`)
}

function caminhoDasEntregas(empresaId: string): string {
  return `/empresas/${encodeURIComponent(empresaId)}/enderecos-de-entrega`
}

/** Acrescenta um endereço de entrega (`201`); `422` sem rótulo ou com CEP/UF tortos. */
export function acrescentarEnderecoDeEntrega(
  empresaId: string,
  entrega: NovoEnderecoDeEntrega,
): Promise<EnderecoDeEntrega> {
  return chamar<EnderecoDeEntrega>('cadastro', caminhoDasEntregas(empresaId), {
    method: 'POST',
    corpo: {
      rotulo: entrega.rotulo,
      endereco:
        entrega.endereco === undefined || entrega.endereco === null
          ? null
          : corpoDoEndereco(entrega.endereco),
    } satisfies NovoEnderecoDeEntrega,
  })
}

/** Remove o endereço de entrega (`204`). O principal não é tocado. */
export function removerEnderecoDeEntrega(empresaId: string, enderecoId: string): Promise<void> {
  return chamar<void>(
    'cadastro',
    `${caminhoDasEntregas(empresaId)}/${encodeURIComponent(enderecoId)}`,
    { method: 'DELETE' },
  )
}

/**
 * Lê as cinco listas de domínio (`200`).
 *
 * Numa instalação nova as cinco vêm vazias, e isso **não** é erro: o seletor renderiza vazio.
 * Nenhum tipo some do corpo, então a tela nunca precisa distinguir "não veio" de "veio vazio".
 */
export function lerOpcoesDeDominio(): Promise<OpcoesDeDominio> {
  return chamar<OpcoesDeDominio>('cadastro', '/opcoes-de-dominio')
}

/** Os campos da ficha, campo a campo — a lista que os dois corpos compartilham. */
function corpoDeEdicao(ficha: EdicaoDeEmpresa): EdicaoDeEmpresa {
  return {
    // Opcional desde a 13.1 (FR-24 emendado): `null` é "sem CNPJ/CPF", e a ficha nasce sem ele.
    documento: ficha.documento ?? null,
    razao_social: ficha.razao_social,
    // 13.7: o nome fantasia é repassado quando veio, e **não** é inventado quando não veio — no
    // `PUT`, a chave ausente é "manter" (o servidor não apaga o que a adoção gravou), e `null` apaga.
    ...(ficha.nome_fantasia === undefined ? {} : { nome_fantasia: ficha.nome_fantasia }),
    categoria_id: ficha.categoria_id,
    setor_id: ficha.setor_id,
    origem_id: ficha.origem_id,
    descricao: ficha.descricao,
    telefone: ficha.telefone,
    email: ficha.email,
    site: ficha.site,
    instagram: ficha.instagram,
    linkedin: ficha.linkedin,
    facebook: ficha.facebook,
    faixa: ficha.faixa,
    endereco:
      ficha.endereco === undefined || ficha.endereco === null
        ? null
        : corpoDoEndereco(ficha.endereco),
    // Delta (FR-79): só vai quando há o que gravar — ausente, o serviço não mexe nos valores.
    ...(ficha.campos_personalizados === undefined || ficha.campos_personalizados === null
      ? {}
      : { campos_personalizados: ficha.campos_personalizados }),
  }
}

/** As sete partes, uma a uma — o corpo do serviço é fechado (`extra="forbid"`). */
function corpoDoEndereco(endereco: CorpoDeEndereco): CorpoDeEndereco {
  return {
    logradouro: endereco.logradouro ?? null,
    numero: endereco.numero ?? null,
    complemento: endereco.complemento ?? null,
    bairro: endereco.bairro ?? null,
    cidade: endereco.cidade ?? null,
    uf: endereco.uf ?? null,
    cep: endereco.cep ?? null,
  }
}

function corpoDeCriacao(ficha: PedidoDeEmpresa): PedidoDeEmpresa {
  return {
    ...corpoDeEdicao(ficha),
    responsavel_id: ficha.responsavel_id,
    // 13.14a (DW-379): as Pessoas só vão quando há; a ficha sem linhas manda o corpo de sempre.
    ...(ficha.pessoas === undefined || ficha.pessoas.length === 0
      ? {}
      : { pessoas: ficha.pessoas.map(corpoDaPessoaDaFichaNova) }),
    // 13.18 (item E): as etiquetas de Empresa e o Grupo, só quando há — sem eles, o serviço não
    // exige `editar_empresa` nem `gerir_grupos_de_empresas`.
    ...(ficha.etiqueta_ids === undefined || ficha.etiqueta_ids.length === 0
      ? {}
      : { etiqueta_ids: [...ficha.etiqueta_ids] }),
    ...(ficha.grupo_id === undefined || ficha.grupo_id === null || ficha.grupo_id === ''
      ? {}
      : { grupo_id: ficha.grupo_id }),
  }
}

function corpoDaPessoaDaFichaNova(
  pessoa: LinhaDaFichaNova | PessoaExistenteNoCorpo,
): LinhaDaFichaNova | PessoaExistenteNoCorpo {
  // 13.19c1: a existente vai só com o `pessoa_id` — o corpo dela é fechado (`extra="forbid"`).
  if ('pessoa_id' in pessoa) {
    return { pessoa_id: pessoa.pessoa_id }
  }
  return {
    ...corpoDePessoa(pessoa),
    ...respostaDaAdocao(pessoa),
  }
}

/** A resposta da pergunta da adoção, só quando ela existe (o padrão do serviço é `perguntar`). */
function respostaDaAdocao(pessoa: {
  adocao?: Adocao
  contato_id?: string | null
}): Partial<Pick<NovaPessoa, 'adocao' | 'contato_id'>> {
  if (pessoa.adocao === undefined || pessoa.adocao === 'perguntar') {
    return {}
  }
  return pessoa.adocao === 'adotar'
    ? { adocao: 'adotar', contato_id: pessoa.contato_id ?? null }
    : { adocao: 'criar_nova' }
}

/**
 * A ficha, para a tela de edição. Sem `id`, a consulta nem sai — é o modo de cadastro.
 *
 * `retry: false` de propósito: a recusa esperada aqui é o `404`, que o serviço devolve tanto
 * para Empresa inexistente quanto para Empresa que quem pediu não vê
 * (FR-30). Nenhuma das duas muda por insistir, e as três tentativas com espera do padrão só
 * deixariam a tela em "carregando" por segundos antes de dizer o que já se sabia no primeiro
 * milissegundo — tempo em que a pessoa não vê nem a ficha nem a explicação.
 */
export function useEmpresa(id: string | undefined) {
  return useQuery({
    queryKey: chaveDaEmpresa(id ?? ''),
    queryFn: () => lerEmpresa(id as string),
    enabled: id !== undefined && id !== '',
    retry: false,
  })
}

/**
 * Uma página da base, com as contagens que o cabeçalho mostra.
 *
 * `retry: false` como as demais consultas do CRM: a recusa esperada é o `401` de sessão
 * ausente, que não muda por insistir.
 *
 * `placeholderData: keepPreviousData` mantém a página anterior na tela enquanto a próxima
 * chega. Sem ele, cada busca e cada clique em "próxima" esvaziariam a tabela por um instante —
 * a tela piscaria "nenhuma empresa encontrada" entre dois resultados, afirmando ausência onde
 * só há espera.
 *
 * O preço é que, nessa janela, os parâmetros da tela **já** avançaram e as linhas ainda são as
 * antigas. Quem paga é `ListaDeEmpresas`: ela lê `isPlaceholderData` para marcar a lista com
 * `aria-busy` e para continuar anunciando a posição que descreve as linhas na tela, em vez de
 * "51–100 · página 2" sobre as cinquenta primeiras fichas.
 */
export function useEmpresas(
  parametros: ParametrosDaListaDeEmpresas = {},
  { habilitada = true }: { habilitada?: boolean } = {},
) {
  return useQuery({
    queryKey: chaveDasEmpresas(parametros),
    queryFn: () => listarEmpresas(parametros),
    retry: false,
    placeholderData: keepPreviousData,
    // A caixa de sugestão do "Novo negócio" (13.1) só pergunta com termo: sem ele, a consulta nem
    // sai — a lista mostra só "Criar nova empresa".
    enabled: habilitada,
  })
}

/** Quantas sugestões cada página do campo de Empresa traz (13.14a, DW-373: "de 10 em 10"). */
export const SUGESTOES_POR_PAGINA = 10

/**
 * As sugestões do campo de Empresa (13.14a, DW-373): **todas**, de 10 em 10, pela semelhança.
 *
 * Uma consulta infinita por termo — a chave mora debaixo de `CHAVE_DA_LISTA_DE_EMPRESAS`, e toda
 * escrita de Empresa que relê a listagem relê as sugestões também. A página seguinte existe enquanto
 * a última veio cheia e as carregadas não chegaram ao `total` (da última página: o total pode mudar
 * entre uma e outra). A ordem é do servidor (`ordem=semelhanca`), e vale entre as páginas.
 */
export function useSugestoesDeEmpresas(termo: string, { habilitada = true } = {}) {
  return useInfiniteQuery<
    PaginaDeEmpresas,
    Error,
    InfiniteData<PaginaDeEmpresas, number>,
    readonly unknown[],
    number
  >({
    queryKey: [...CHAVE_DA_LISTA_DE_EMPRESAS, 'sugestoes', termo],
    queryFn: ({ pageParam }) =>
      listarEmpresas({
        busca: termo,
        ordem: 'semelhanca',
        limite: SUGESTOES_POR_PAGINA,
        pagina: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (ultima, paginas, ultimaPagina) => {
      const carregadas = paginas.reduce((soma, pagina) => soma + pagina.itens.length, 0)
      return ultima.itens.length === SUGESTOES_POR_PAGINA && carregadas < ultima.total
        ? ultimaPagina + 1
        : undefined
    },
    retry: false,
    placeholderData: keepPreviousData,
    enabled: habilitada,
  })
}

/**
 * O tamanho de cada lote de `ids` — o mesmo `LIMITE_MAXIMO` do serviço.
 *
 * Ele é o teto do parâmetro na rota (`422` acima dele) e o teto da página, e é por isso que o
 * lote e a página cabem no mesmo orçamento de consulta. Um quadro com mais de 200 Empresas
 * distintas vira dois pedidos, e não um `422`.
 */
const TAMANHO_DO_LOTE = LIMITE_MAXIMO

/**
 * O que `useNomesDeEmpresas` devolve: o mapa **e** o estado dos lotes que o produziram.
 *
 * O mapa sozinho não bastava, e o defeito era de produto: um `id` fora dele quer dizer três
 * coisas muito diferentes — o lote ainda está no ar, o lote falhou, ou a policy do `cadastro`
 * não devolveu aquela ficha. Só a terceira é a degradação documentada ("existe, e a ficha não é
 * sua"); as outras duas são transitórias ou defeito, e tratá-las como a terceira faria a tela
 * afirmar, sobre a carteira inteira, algo que ela não sabe.
 */
export interface NomesDeEmpresas {
  /**
   * `id -> nome exibido` (13.7): o nome fantasia, e a razão social sem ele. O que a policy não
   * devolveu simplesmente não está aqui.
   */
  nomes: ReadonlyMap<string, string>
  /**
   * `id -> razao_social` (13.7): para as buscas locais (o Funil, o WhatsApp) acharem a Empresa
   * também pela razão social, que a tela não mostra mais.
   */
  razoesSociais: ReadonlyMap<string, string>
  /** `id -> etiqueta_ids` (13.6): as etiquetas da equipe de cada ficha do lote. */
  etiquetas: ReadonlyMap<string, readonly string[]>
  /** Algum lote ainda está no ar — a ausência de um `id` no mapa ainda não quer dizer nada. */
  carregando: boolean
  /** Algum lote falhou. O que falta não é sigilo: é a resposta que não chegou. */
  falhou: boolean
}

/**
 * Resolve o nome exibido (13.7: o nome fantasia, e a razão social sem ele) de um conjunto de
 * Empresas, num mapa `id -> nome`.
 *
 * **É a peça que o AD-25 exige.** O card do quadro do Funil (3.8) mostra o nome da Empresa, e o
 * serviço `negocios` não o tem: ele não guarda réplica de Empresa (AD-21, AD-22), e resolvê-lo
 * lá com token de serviço entregaria à tela a razão social que a policy do `cadastro` esconde
 * daquele usuário (FR-30) — os dois pisos são independentes de propósito. Com o SPA resolvendo
 * pelo JWT de quem está olhando, cada serviço continua dono do seu piso.
 *
 * **O que a policy não devolver simplesmente não entra no mapa**, e quem chamou degrada — o card
 * cai no **código** do Negócio, que é o identificador que a operação já usa na boca (AD-12).
 * Nenhum erro é mostrado por isso: uma ficha invisível é o caso raro e legítimo, e não uma falha.
 *
 * `useQueries` sobre **lotes**, e não uma consulta por id: um quadro de trezentos cards seriam
 * trezentas idas à rede. É o mesmo molde de `useRotulosDeOpcoes`, com uma diferença que vem do
 * serviço — lá cada rótulo tem rota própria, aqui a listagem aceita o lote inteiro de uma vez.
 *
 * `retry: false` porque a recusa esperada não muda por insistir, e `staleTime` alto porque razão
 * social quase não muda: reconsultar a cada montagem gastaria uma ida à rede por abertura de
 * quadro. Quem edita a ficha invalida a listagem inteira (`useInvalidarAsEmpresas`), e o prefixo
 * dela alcança estes lotes.
 */
export function useNomesDeEmpresas(ids: readonly string[]): NomesDeEmpresas {
  const lotes = useMemo(() => {
    // Ordenado e sem repetição: a chave de cache é derivada do lote, e duas telas com os mesmos
    // ids em ordens diferentes devem cair na mesma entrada. Sem o `sort`, cada renderização com
    // uma ordem nova seria uma consulta nova para o mesmo conjunto.
    const unicos = [...new Set(ids)].sort()
    const partes: string[][] = []
    for (let inicio = 0; inicio < unicos.length; inicio += TAMANHO_DO_LOTE) {
      partes.push(unicos.slice(inicio, inicio + TAMANHO_DO_LOTE))
    }
    return partes
  }, [ids])

  return useQueries({
    queries: lotes.map((lote) => ({
      queryKey: chaveDasEmpresas({ ids: lote, limite: TAMANHO_DO_LOTE }),
      queryFn: () => listarEmpresas({ ids: lote, limite: TAMANHO_DO_LOTE }),
      retry: false,
      staleTime: 5 * 60 * 1000,
    })),
    combine: combinarOsLotes,
  })
}

/**
 * O `combine` de `useNomesDeEmpresas` — **no módulo**, e não uma seta dentro do componente.
 *
 * Ele devolve os três: o mapa, se **algum** lote ainda está no ar e se **algum** falhou. Antes
 * ele engolia a falha num `?? []` e só o mapa saía daqui — e quem chamava não tinha como separar
 * "ainda não chegou" de "a policy não devolveu". `some`, e não `every`, nos dois: um lote pendente
 * já basta para o mapa estar incompleto, e um lote falho já basta para uma parte dos nomes nunca
 * chegar.
 *
 * **A identidade estável é o ponto, e ela é de desempenho.** O `useQueries` só reaproveita o
 * resultado anterior enquanto a função `combine` for a **mesma**; declarada em linha, ela nasce
 * nova a cada renderização, o `combine` roda de novo e devolve um `Map` novo — que nenhuma
 * comparação estrutural consegue reaproveitar. Aquele `Map` é dependência dos `useMemo` que
 * montam os cards e as colunas do quadro, e com identidade nova toda renderização eles refariam
 * a passada inteira sobre centenas de Negócios. É o orçamento da NFR-3 sendo gasto em trabalho
 * que ninguém pediu.
 */
function combinarOsLotes(
  resultados: readonly { data?: PaginaDeEmpresas; isPending: boolean; isError: boolean }[],
): NomesDeEmpresas {
  return {
    nomes: new Map(
      resultados
        .flatMap((resultado) => resultado.data?.itens ?? [])
        .map((empresa) => [empresa.id, nomeExibidoDaEmpresa(empresa)] as const),
    ),
    razoesSociais: new Map(
      resultados
        .flatMap((resultado) => resultado.data?.itens ?? [])
        .map((empresa) => [empresa.id, empresa.razao_social] as const),
    ),
    etiquetas: new Map(
      resultados
        .flatMap((resultado) => resultado.data?.itens ?? [])
        .map((empresa) => [empresa.id, etiquetaIdsDe(empresa)] as const),
    ),
    carregando: resultados.some((resultado) => resultado.isPending),
    falhou: resultados.some((resultado) => resultado.isError),
  }
}

/**
 * O vocabulário das cinco listas.
 *
 * `retry: false` porque a resposta vazia é a resposta certa numa instalação nova: repetir a
 * chamada não faria surgir lista nenhuma, e a tela sabe desenhar seletor vazio.
 */
export function useOpcoesDeDominio() {
  return useQuery({ queryKey: CHAVE_DAS_OPCOES, queryFn: lerOpcoesDeDominio, retry: false })
}

/**
 * Invalida **todas** as páginas da listagem — toda página, todo termo.
 *
 * As mutações da Empresa a chamam, e nenhuma delas pode se dar ao luxo de não chamar:
 * criar acrescenta uma linha e move o `total`; editar pode mudar a razão social, que
 * é a coluna da **ordem** (a ficha muda de página); o Responsável muda **quem**
 * enxerga a linha, que é o que a RLS recorta. Sem isto, o fluxo que esta própria tela cria —
 * listagem → "Nova empresa" → salvar → voltar — mostraria a página velha e o subtítulo velho
 * pelos 30 segundos de `staleTime` do cliente de consultas.
 *
 * A promessa é **devolvida**, como em `useInvalidarAsOpcoes`: `onSuccess` a aguarda, e é isso
 * que mantém a mutação "pendente" até a listagem já refletir o ato.
 */
function useInvalidarAsEmpresas() {
  const clienteDeConsultas = useQueryClient()
  // 13.9: a ficha do Grupo mostra nome, CNPJ e Responsável das filiais — toda mutação de Empresa
  // que relê a listagem relê também os Grupos em cache.
  return () =>
    Promise.all([
      clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DA_LISTA_DE_EMPRESAS }),
      clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_GRUPOS }),
    ])
}

/** Cria a ficha e a deixa no cache — a tela seguinte não paga um `GET` para saber o que já veio. */
export function useCriarEmpresa() {
  const clienteDeConsultas = useQueryClient()
  const relerAListagem = useInvalidarAsEmpresas()
  return useMutation({
    mutationFn: (ficha: PedidoDeEmpresa) => criarEmpresa(ficha),
    /*
     * 13.18: a recusa da ficha nova com etiquetas ou Grupo (`422` da etiqueta apagada ou de outro
     * tipo, `404` do Grupo que sumiu) diz que o catálogo e a lista de Grupos desta aba envelheceram:
     * os dois são relidos, e a escolha se poda sozinha.
     */
    onError: (_falha, ficha: PedidoDeEmpresa) => {
      const levou =
        (ficha.etiqueta_ids?.length ?? 0) > 0 ||
        (ficha.grupo_id !== undefined && ficha.grupo_id !== null && ficha.grupo_id !== '')
      if (!levou) {
        return undefined
      }
      return Promise.all([
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DAS_ETIQUETAS }),
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_GRUPOS }),
      ])
    },
    onSuccess: (criada: EmpresaCriada) => {
      // O cache da ficha guarda a ficha, e não a lista das Pessoas que nasceram com ela.
      const pessoas = pessoasDaCriada(criada)
      const empresa: Empresa = { ...criada }
      delete (empresa as Partial<EmpresaCriada>).pessoas
      clienteDeConsultas.setQueryData(chaveDaEmpresa(empresa.id), empresa)
      // A ficha vem pronta na resposta; a **listagem** não — e o que mudou aqui move a linha de
      // lugar, de página ou de quem a enxerga. Invalidar é o preço de a listagem não mentir.
      // Com Pessoas (13.14a), a lista da ficha, a tela Pessoas e os lotes do WhatsApp (uma linha
      // pode ter adotado a solta de uma conversa) também.
      return Promise.all([
        relerAListagem(),
        ...(pessoas.length === 0
          ? []
          : [
              clienteDeConsultas.invalidateQueries({ queryKey: chaveDasPessoas(empresa.id) }),
              clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_LOTES_DE_PESSOAS }),
            ]),
      ])
    },
  })
}

/**
 * Consulta o documento no ERP — e invalida a listagem **quando** a consulta criou uma ficha.
 *
 * É uma mutação, e não uma consulta, e a escolha não é de gosto: ela é `POST`, ela **escreve** (a
 * situação `criada` grava uma Empresa) e ela nasce de um ato — o campo perdendo o foco —, e não de
 * uma tela precisando de dado. Como `useQuery` ela seria refeita a cada montagem e teria cache,
 * que é exatamente o que o FR-46 proíbe: leitura do ERP é sob demanda, sempre.
 *
 * A invalidação é **condicional** pela mesma razão: quando o desfecho é `inedito` ou `existente`
 * nada mudou na base, e invalidar ali custaria uma ida à rede por CNPJ digitado.
 *
 * **A ficha nova não é gravada em `chaveDaEmpresa`**, ao contrário do que `useCriarEmpresa` faz: a
 * resposta desta rota tem dois campos, e não a ficha inteira. Quem navega para ela a lê por
 * `GET /empresas/{id}`, como em qualquer outra abertura.
 */
export function useConsultarDocumento() {
  const relerAListagem = useInvalidarAsEmpresas()
  return useMutation({
    mutationFn: ({ documento, nomeFantasia }: { documento: string; nomeFantasia?: string }) =>
      consultarDocumento(documento, nomeFantasia),
    onSuccess: (consulta: ConsultaDeDocumento) =>
      consulta.situacao === 'criada' ? relerAListagem() : undefined,
  })
}

/**
 * As consultas públicas como **mutação**, e não consulta: nascem de um ato (o campo perdendo o
 * foco), e o AD-28 proíbe cache de dado de terceiro — um `useQuery` guardaria a resposta.
 */
export function useConsultarCnpj() {
  return useMutation({ mutationFn: (cnpj: string) => consultarCnpj(cnpj) })
}

/** Irmã de `useConsultarCnpj`, para o CEP. */
export function useConsultarCep() {
  return useMutation({ mutationFn: (cep: string) => consultarCep(cep) })
}

/** Acrescenta a entrega e relê a ficha: a lista aninhada mora na resposta dela. */
export function useAcrescentarEnderecoDeEntrega(empresaId: string) {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: (entrega: NovoEnderecoDeEntrega) =>
      acrescentarEnderecoDeEntrega(empresaId, entrega),
    onSuccess: () => clienteDeConsultas.invalidateQueries({ queryKey: chaveDaEmpresa(empresaId) }),
  })
}

/** Remove a entrega e relê a ficha. */
export function useRemoverEnderecoDeEntrega(empresaId: string) {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: (enderecoId: string) => removerEnderecoDeEntrega(empresaId, enderecoId),
    onSuccess: () => clienteDeConsultas.invalidateQueries({ queryKey: chaveDaEmpresa(empresaId) }),
  })
}

/**
 * Grava no cache a ficha que uma escrita devolveu **com os `etiqueta_ids` que o cache já tem**
 * (13.14a, DW-411).
 *
 * Nem o `PUT` da ficha nem a entrega mexem nas etiquetas, e a resposta delas traz as etiquetas lidas
 * na transação delas: se um aplicar estava em voo, a resposta chegando depois do aplicar regravaria
 * o cache sem o chip recém-aplicado, e ele sumiria até a próxima leitura. As etiquetas do cache são
 * sempre ao menos tão novas quanto as da resposta — aplicar e retirar invalidam a ficha. Sem a ficha
 * no cache, vale a resposta.
 */
function gravarAFichaComAsEtiquetasDoCache(
  clienteDeConsultas: ReturnType<typeof useQueryClient>,
  empresa: Empresa,
) {
  clienteDeConsultas.setQueryData<Empresa>(chaveDaEmpresa(empresa.id), (atual) =>
    atual === undefined ? empresa : { ...empresa, etiqueta_ids: atual.etiqueta_ids },
  )
}

/** Grava a ficha e atualiza o cache com **a resposta do servidor**, e não com o que foi enviado. */
export function useEditarEmpresa(id: string) {
  const clienteDeConsultas = useQueryClient()
  const relerAListagem = useInvalidarAsEmpresas()
  return useMutation({
    mutationFn: (ficha: EdicaoDeEmpresa) => editarEmpresa(id, ficha),
    onSuccess: (empresa: Empresa) => {
      gravarAFichaComAsEtiquetasDoCache(clienteDeConsultas, empresa)
      // A ficha vem pronta na resposta; a **listagem** não — e o que mudou aqui move a linha de
      // lugar, de página ou de quem a enxerga. Invalidar é o preço de a listagem não mentir.
      // A tela Pessoas mostra o nome da Empresa (13.7), e também é relida.
      return Promise.all([
        relerAListagem(),
        clienteDeConsultas.invalidateQueries({
          queryKey: [...CHAVE_DOS_LOTES_DE_PESSOAS, 'lista'],
        }),
      ])
    },
  })
}

/**
 * Entrega a ficha e grava **a resposta do servidor** em `chaveDaEmpresa(id)`.
 *
 * Mesmo desfecho da edição da ficha, e pela mesma razão: a resposta é a ficha inteira, e
 * é dessa chave que a tela de edição vive. Gravar o que foi **enviado** mostraria o Responsável
 * novo mesmo quando o serviço tivesse gravado outro; invalidar custaria uma ida à rede para
 * receber o que já veio.
 */
export function useTrocarResponsavel(id: string) {
  const clienteDeConsultas = useQueryClient()
  const relerAListagem = useInvalidarAsEmpresas()
  return useMutation({
    mutationFn: (responsavelId: string) => trocarResponsavel(id, responsavelId),
    onSuccess: (empresa: Empresa) => {
      gravarAFichaComAsEtiquetasDoCache(clienteDeConsultas, empresa)
      // A ficha vem pronta na resposta; a **listagem** não — e o que mudou aqui move a linha de
      // lugar, de página ou de quem a enxerga. Invalidar é o preço de a listagem não mentir.
      return relerAListagem()
    },
  })
}

/**
 * Reenvia a sincronização e **relê a ficha nos dois desfechos** (`onSettled`).
 *
 * `onSettled`, e não `onSuccess`, e a diferença é justamente o caso em que a tela está errada. As
 * duas recusas esperadas aqui dizem a mesma coisa: **a ficha mudou desde a última leitura**. Um
 * `409` significa que ela deixou de estar pendente (o trabalhador venceu a corrida com o clique);
 * um `404`, que ela sumiu ou passou a ser invisível. Invalidar só no sucesso deixaria o selo velho
 * na tela com o botão ativo, e o clique seguinte repetiria o mesmo erro — a tela insistindo num
 * estado que o serviço já não tem. Relendo nos dois caminhos, o erro aparece **uma vez** e a tela
 * passa a mostrar o que existe.
 *
 * A ficha vem pronta na resposta e ainda assim ela é **invalidada**, e não gravada com
 * `setQueryData`: é justamente porque o caminho de erro também precisa reler, e um único caminho
 * de atualização é melhor que dois que podem divergir.
 *
 * **A listagem não é invalidada**, ao contrário das quatro mutações da ficha: a pendência de
 * sincronização não é coluna da tabela, não move a linha de página e não muda quem a enxerga.
 */
export function useReenviarSincronizacao(id: string) {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: () => reenviarSincronizacao(id),
    onSettled: () => clienteDeConsultas.invalidateQueries({ queryKey: chaveDaEmpresa(id) }),
  })
}

/* --- Excluir a Empresa (13.12, FR-89) ------------------------------------------------- */

/**
 * A prévia da confirmação: quantos Negócios a exclusão esconde, quantos deles estão em andamento
 * com outras pessoas e o bloqueio da PA-5 (a mensagem do `409`, ou `null`). **Só números** — sem id,
 * sem nome e sem valor (FR-62).
 */
export type PreviaDaExclusao = Esquemas['RespostaDaPreviaDaExclusao']

/** Fora do prefixo da ficha (`['cadastro', 'empresa', id]`): as releituras da ficha não a tocam. */
export const chaveDaPreviaDaExclusao = (id: string) => ['cadastro', 'exclusao', id] as const

/** `GET /empresas/{id}/exclusao` — a mesma guarda e o mesmo `404` do `DELETE` (D-5). */
export function lerAPreviaDaExclusao(id: string): Promise<PreviaDaExclusao> {
  return chamar<PreviaDaExclusao>('cadastro', `/empresas/${encodeURIComponent(id)}/exclusao`)
}

/** `DELETE /empresas/{id}` — `204` sem corpo; a Empresa é escondida, e não apagada (opção C). */
export function excluirEmpresa(id: string): Promise<void> {
  return chamar<void>('cadastro', `/empresas/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

/**
 * A prévia, lida **a cada abertura** da confirmação: `staleTime: 0` e `gcTime: 0` — um Negócio que
 * nasceu entre duas aberturas tem de aparecer na contagem, e a prévia velha no cache diria um número
 * que o "sim" já não confirma. Quem monta só a monta com o diálogo aberto.
 */
export function usePreviaDaExclusao(id: string) {
  return useQuery({
    queryKey: chaveDaPreviaDaExclusao(id),
    queryFn: () => lerAPreviaDaExclusao(id),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })
}

/**
 * Exclui a Empresa e relê **o que continua existindo**: a lista e os lotes de nomes do `cadastro`,
 * as Pessoas, os Grupos, e os módulos onde os Negócios e as conversas dela apareciam.
 *
 * **A ficha não é invalidada aqui.** Ela ainda está montada quando o `onSuccess` deste hook roda,
 * e o `GET` refeito receberia o `404` da Empresa que acabou de sair — a tela piscaria a recusa no
 * caminho de saída. Quem a tira do cache é `useEsquecerAEmpresa`, **depois** de a tela sair dela
 * (o molde de `useExcluirNegocio`). Os prefixos dos outros módulos vão literais, e não importados:
 * o `cadastro` não importa de `negocios`, `relatorios` nem `comunicacao`.
 */
export function useExcluirEmpresa(id: string) {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: () => excluirEmpresa(id),
    onSuccess: () =>
      Promise.all(
        [
          CHAVE_DA_LISTA_DE_EMPRESAS,
          CHAVE_DOS_LOTES_DE_PESSOAS,
          CHAVE_DOS_GRUPOS,
          ['negocios'] as const,
          ['relatorios'] as const,
          ['comunicacao'] as const,
        ].map((queryKey) => clienteDeConsultas.invalidateQueries({ queryKey })),
      ),
  })
}

/** Tira a ficha, as Pessoas e a prévia da Empresa excluída do cache — depois de a tela sair. */
export function useEsquecerAEmpresa() {
  const clienteDeConsultas = useQueryClient()
  return (id: string) => {
    clienteDeConsultas.removeQueries({ queryKey: chaveDaEmpresa(id) })
    clienteDeConsultas.removeQueries({ queryKey: chaveDasPessoas(id) })
    clienteDeConsultas.removeQueries({ queryKey: chaveDaPreviaDaExclusao(id) })
  }
}

/* --- Responsáveis pela empresa (história 2.2) ------------------------------------- */

/**
 * Um Responsável pela empresa, como as rotas de Pessoa o devolvem.
 *
 * **Desde a 13.19c2 a Pessoa traz as Empresas que quem pede enxerga** (`empresa_ids`, na ordem do
 * vínculo; vazia na solta) e, delas, as que quem pede edita (`empresa_ids_editaveis`: a base, o
 * Grupo ou a PA-1 — nunca uma vista só pela PA-3). Uma Empresa invisível não é nomeada nem contada.
 * As três perguntas que as telas fazem saem daqui, e não de um `empresa_id` único: é solta?
 * (`ehSolta`), é desta Empresa? (`estaNaEmpresa`), qual Empresa preencher? (a única, quando há uma).
 */
export type Pessoa = Esquemas['RespostaDePessoa']

/** A Pessoa sem Empresa visível nenhuma — a solta (13.19c2: a lista vazia). */
export function ehSolta(pessoa: Pick<Pessoa, 'empresa_ids'>): boolean {
  //: Sem o campo (o `cadastro` anterior à 13.19c2, no rollback) é "não sei", e não "solta".
  return empresaIdsDe(pessoa)?.length === 0
}

/**
 * As Empresas visíveis da Pessoa, ou `undefined` quando o `cadastro` não as mandou (o anterior à
 * 13.19c2, no rollback): quem lê decide o que "não sei" quer dizer, sem `TypeError`.
 */
export function empresaIdsDe(pessoa: Pick<Pessoa, 'empresa_ids'>): readonly string[] | undefined {
  const lista = (pessoa as { empresa_ids?: unknown }).empresa_ids
  return Array.isArray(lista) ? (lista as string[]) : undefined
}

/** As Empresas por que quem olha edita a Pessoa — vazia quando o campo não veio. */
export function empresaIdsEditaveisDe(
  pessoa: Pick<Pessoa, 'empresa_ids_editaveis'>,
): readonly string[] {
  const lista = (pessoa as { empresa_ids_editaveis?: unknown }).empresa_ids_editaveis
  return Array.isArray(lista) ? (lista as string[]) : []
}

/** A Pessoa já está **nesta** Empresa (entre as visíveis)? Sem o campo, não se sabe: `false`. */
export function estaNaEmpresa(pessoa: Pick<Pessoa, 'empresa_ids'>, empresaId: string): boolean {
  return empresaIdsDe(pessoa)?.includes(empresaId) ?? false
}

/**
 * A Empresa por que quem olha edita a Pessoa vinculada: a primeira das editáveis (13.19c2), ou
 * `undefined` quando ele só a vê (pela PA-3) — e então não há edição. As editáveis são também as
 * Empresas em que quem olha **cria Negócio** (o `negocio_insercao` do `negocios`; code review da
 * 13.19c2, C — a diferença está no Spec Change Log).
 */
export function empresaDaEdicao(pessoa: Pick<Pessoa, 'empresa_ids_editaveis'>): string | undefined {
  return empresaIdsEditaveisDe(pessoa)[0]
}

/**
 * O que o formulário envia para cadastrar. Só `nome` é obrigatório (FR-26).
 *
 * `principal` entra aqui, e só aqui: cadastrar já como principal é um caminho de verdade, e
 * marcar desmarca a anterior na mesma transação do serviço.
 */
export type NovaPessoa = Esquemas['CorpoDeNovaPessoa']

/** O cadastro como a tela o pede: a resposta da adoção (13.14a) só quando a pergunta foi feita. */
export type PedidoDePessoa = Omit<NovaPessoa, 'adocao'> & Partial<Pick<NovaPessoa, 'adocao'>>

/**
 * O que o formulário envia para editar: os quatro campos, **sem** `principal`.
 *
 * Marcar o principal tem rota própria porque desmarca outra linha; enviá-lo aqui é `422`, e
 * não um campo ignorado em silêncio.
 */
export type EdicaoDePessoa = Esquemas['CorpoDeEdicaoDePessoa']

/**
 * O que o cadastro devolve desde a 13.4: o Responsável **e** `adotada`.
 *
 * `adotada: true` quer dizer que o telefone era o de exatamente uma Pessoa solta que quem cadastra
 * enxerga (a que ele recebeu pelo WhatsApp, ou qualquer uma para a gestão), e que **ela** foi
 * completada e vinculada: o `id` é o dela, e nenhuma Pessoa nasceu (PN2). A tela avisa
 * `CONTATO_DO_WHATSAPP_VINCULADO`. Um backend anterior à 13.4 não manda o campo — e ausente vale
 * `false` (`foiAdotada`).
 */
export type PessoaCadastrada = Esquemas['RespostaDoCadastroDePessoa']

/** O aviso da adoção (13.4), o mesmo na ficha, no "+ Empresa", no modal e na tela Pessoas. */
export const CONTATO_DO_WHATSAPP_VINCULADO = 'Contato do WhatsApp vinculado a esta empresa.'

/** A resposta do cadastro adotou a Pessoa solta? Ausente (backend velho) é `false`. */
export function foiAdotada(pessoa: Partial<Pick<PessoaCadastrada, 'adotada'>>): boolean {
  return pessoa.adotada === true
}

/** Chave de cache da lista de Responsáveis de uma Empresa. */
export const chaveDasPessoas = (empresaId: string) =>
  ['cadastro', 'empresa', empresaId, 'pessoas'] as const

function caminhoDasPessoas(empresaId: string): string {
  return `/empresas/${encodeURIComponent(empresaId)}/pessoas`
}

/**
 * Lê os Responsáveis da Empresa (`200`) — a principal primeiro, depois por nome.
 *
 * Lista vazia é `[]`, nunca `404`: a ficha recém-criada não tem contato nenhum. O `404` aqui
 * é o da **Empresa** (inexistente ou invisível a quem pede, FR-30), e a mensagem dele
 * vai à tela verbatim.
 */
export function lerPessoas(empresaId: string): Promise<Pessoa[]> {
  return chamar<Pessoa[]>('cadastro', caminhoDasPessoas(empresaId))
}

/**
 * Cadastra o Responsável (`201`). Exige a permissão `editar_empresa`.
 *
 * Os campos vão **um a um**, e não o objeto que chegou: o corpo do serviço é fechado
 * (`extra="forbid"`), e um objeto de formulário com um campo a mais viraria um `422` genérico
 * que não diz qual campo sobrou.
 */
export function cadastrarPessoa(
  empresaId: string,
  pessoa: PedidoDePessoa,
): Promise<PessoaCadastrada> {
  return chamar<PessoaCadastrada>('cadastro', caminhoDasPessoas(empresaId), {
    method: 'POST',
    corpo: { ...corpoDePessoa(pessoa), principal: pessoa.principal, ...respostaDaAdocao(pessoa) },
  })
}

/**
 * Grava os quatro campos (`200`). Exige a permissão `editar_empresa`.
 *
 * **Reenviar os mesmos valores não deixa registro na trilha** — o serviço compara antes de
 * gravar —, e nenhum dos quatro campos publica evento: eles não mudam o vínculo.
 */
export function editarPessoa(
  empresaId: string,
  pessoaId: string,
  pessoa: EdicaoDePessoa,
): Promise<Pessoa> {
  return chamar<Pessoa>(
    'cadastro',
    `${caminhoDasPessoas(empresaId)}/${encodeURIComponent(pessoaId)}`,
    { method: 'PUT', corpo: corpoDePessoa(pessoa) },
  )
}

/**
 * Marca o Responsável como principal (`200`). Exige a permissão `editar_empresa`.
 *
 * Não há corpo: a rota **é** o ato. A anterior é desmarcada na mesma transação, e marcar quem
 * já é principal responde `200` sem deixar trilha.
 */
export function marcarPessoaComoPrincipal(empresaId: string, pessoaId: string): Promise<Pessoa> {
  return chamar<Pessoa>(
    'cadastro',
    `${caminhoDasPessoas(empresaId)}/${encodeURIComponent(pessoaId)}/principal`,
    { method: 'PUT' },
  )
}

/** Os quatro campos, campo a campo — a lista que os dois corpos compartilham. */
function corpoDePessoa(pessoa: EdicaoDePessoa): EdicaoDePessoa {
  return {
    nome: pessoa.nome,
    telefone: pessoa.telefone,
    email: pessoa.email,
    cargo: pessoa.cargo,
  }
}

/**
 * A lista de Responsáveis da Empresa.
 *
 * `retry: false` pela mesma razão de `useEmpresa`: a recusa esperada é o `404` da Empresa
 * inexistente ou escondida (FR-30), e nenhuma das duas muda por insistir.
 *
 * **`enabled` e `isPending` não são a mesma pergunta.** Sem `empresaId` a consulta não sai, e
 * no React Query v5 uma consulta desabilitada sem cache fica `pending` **para sempre** — quem
 * decidir o ramo de carregamento por `isPending` prende a tela em "carregando" sem nunca ter
 * pedido nada. O ramo de carregamento da seção usa `isLoading`, que é `pending` **e**
 * buscando; aqui o comentário existe para que a próxima tela que consumir este hook não caia
 * na mesma armadilha.
 */
export function usePessoas(empresaId: string) {
  return useQuery({
    queryKey: chaveDasPessoas(empresaId),
    queryFn: () => lerPessoas(empresaId),
    enabled: empresaId !== '',
    retry: false,
  })
}

/**
 * As três mutações compartilham o mesmo desfecho: **reler a lista do serviço**.
 *
 * Invalidar, e não remendar o cache com a resposta, é o ponto: cadastrar ou marcar como
 * principal muda **outra** linha além da devolvida (a que perdeu a marca), e um `setQueryData`
 * com a resposta deixaria duas marcadas na tela até a próxima navegação — exatamente o estado
 * que o serviço existe para impedir.
 */
function useInvalidarAsPessoas(empresaId: string) {
  const clienteDeConsultas = useQueryClient()
  return () => clienteDeConsultas.invalidateQueries({ queryKey: chaveDasPessoas(empresaId) })
}

/**
 * Cadastra o Responsável e relê a lista — e, desde a 13.4, os lotes de Pessoas.
 *
 * O cadastro pode **adotar** a Pessoa solta (PN2): o nome e a Empresa de um contato que a tela de
 * Conversa, o painel e a tela Pessoas já mostravam mudaram. O prefixo `['cadastro', 'pessoas']`
 * alcança todos esses lotes.
 */
export function useCadastrarPessoa(empresaId: string) {
  const aoTerminar = useInvalidarAsPessoas(empresaId)
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: (pessoa: PedidoDePessoa) => cadastrarPessoa(empresaId, pessoa),
    onSuccess: () =>
      Promise.all([
        aoTerminar(),
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_LOTES_DE_PESSOAS }),
      ]),
  })
}

/** Grava os quatro campos e relê a lista. */
export function useEditarPessoa(empresaId: string) {
  const aoTerminar = useReleituraDaPessoaEditada(empresaId)
  return useMutation({
    mutationFn: ({ pessoaId, pessoa }: { pessoaId: string; pessoa: EdicaoDePessoa }) =>
      editarPessoa(empresaId, pessoaId, pessoa),
    onSuccess: aoTerminar,
  })
}

/** Marca o Responsável como principal e relê a lista — a anterior perdeu a marca. */
export function useMarcarPrincipal(empresaId: string) {
  const aoTerminar = useReleituraDaPessoaEditada(empresaId)
  return useMutation({
    mutationFn: (pessoaId: string) => marcarPessoaComoPrincipal(empresaId, pessoaId),
    onSuccess: aoTerminar,
  })
}

/**
 * O desfecho da edição pela ficha (13.19c2, code review E6): a lista da Empresa do caminho, os
 * lotes de Pessoas (o painel, a tela Pessoas, a conversa) e a lista de **cada** Empresa visível da
 * Pessoa — o mesmo contato aparece na ficha de todas.
 */
function useReleituraDaPessoaEditada(empresaId: string) {
  const clienteDeConsultas = useQueryClient()
  return (pessoa: Pessoa) =>
    Promise.all([
      clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_LOTES_DE_PESSOAS }),
      ...[...new Set([empresaId, ...(empresaIdsDe(pessoa) ?? [])])].map((id) =>
        clienteDeConsultas.invalidateQueries({ queryKey: chaveDasPessoas(id) }),
      ),
    ])
}

/* --- O lote de Pessoas por id (história 8.3, AD-25) ------------------------------- */

/**
 * O prefixo de **todo** cache de Pessoas fora da ficha: os lotes por `id` (8.3), a releitura do
 * painel (13.3) e as páginas da tela Pessoas (13.4). Quem muda nome ou Empresa de um contato
 * invalida este prefixo.
 */
export const CHAVE_DOS_LOTES_DE_PESSOAS = ['cadastro', 'pessoas'] as const

/**
 * Chave de cache de **um lote** de Pessoas.
 *
 * O lote entra na chave como texto ordenado, e não como array: o react-query compara a chave por
 * estrutura, e ali a ordem importa — para o serviço, não. Ordenar antes faz duas telas com os
 * mesmos ids em ordens diferentes caírem na mesma entrada, que é o que elas são. É o mesmo
 * tratamento que `normalizarParametros` dá ao `?ids=` de Empresas.
 */
export const chaveDoLoteDePessoas = (ids: readonly string[]) =>
  ['cadastro', 'pessoas', [...ids].sort().join(',')] as const

/**
 * Resolve um lote de Pessoas por `id` (`200`).
 *
 * **`ids` é obrigatório no serviço**, e um lote vazio nem chega a virar URL: `?ids=` sem valor é
 * `422` (string vazia não é uuid), e quem pede zero fichas já sabe que recebe zero. É o mesmo corte
 * que `listarEmpresas` faz, pela mesma razão.
 *
 * **O `id` que a policy não devolve simplesmente não está na resposta**, e isso não é `404`: quem
 * chamou degrada para o que já tem. Na tela de Conversa, o contato cai para o **número**, que é o
 * nome que a 8.2 já lhe dá.
 */
export function lerPessoasPorIds(ids: readonly string[]): Promise<Pessoa[]> {
  if (ids.length === 0) {
    return Promise.resolve([])
  }
  const consulta = new URLSearchParams()
  // Um par `ids=` por valor, que é o formato que o FastAPI lê para uma lista de uuid. Um único
  // `ids=a,b` chegaria como um uuid malformado e viraria `422`.
  for (const id of ids) {
    consulta.append('ids', id)
  }
  return chamar<Pessoa[]>('cadastro', `/pessoas?${consulta.toString()}`)
}

/** O vínculo de uma Pessoa solta a uma Empresa, como a rota o devolve (8.2, FR-40). */
export type VinculoComEmpresa = Esquemas['RespostaDoVinculoComEmpresa']

/**
 * Vincula a Pessoa a uma Empresa (`200`) — `POST /pessoas/{pessoa_id}/empresa`.
 *
 * É a porta do FR-40, e o "+ Negócio para esta pessoa" do WhatsApp (13.1) é o primeiro chamador do
 * SPA: criado o Negócio, a **mesma** Pessoa solta da conversa entra na Empresa dele. Quem chama
 * relê a Pessoa antes (`lerPessoasPorIds`) e só vincula a que ainda está solta.
 *
 * **`exigirSolta` (13.3, D-6) é a trava de backend que a 13.1 esperava**: com ela, a Pessoa que já
 * está noutra Empresa volta `409` ("Este contato ja foi vinculado a outra Empresa.") e a mesma
 * Empresa continua idempotente. Até a 13.19a2, sem ela a rota **movia** a Pessoa já vinculada (a
 * 8.2); desde a 13.19a2 ninguém move Pessoa, e o `409` vale com ou sem ela — até a 13.19b2, que faz
 * o vínculo somar. Nenhum chamador do SPA manda `false`.
 * O painel "Dados do contato" e o Novo negócio mandam `true`: entre a releitura e o `POST`, outra
 * aba pode ter vinculado o contato, e o serviço é quem fecha essa corrida. **O campo só vai quando é
 * `true`**: o corpo do serviço é fechado (`extra="forbid"`), e um backend anterior à 13.3 — o
 * rollback da VPS antes da Vercel — recusaria com `422` todo vínculo que levasse `false`.
 *
 * Exige `editar_empresa`. `404` quando a Empresa não existe ou a RLS a esconde, e `404` de Pessoa —
 * inclusive a solta de outro recebedor, que a RLS esconde desde a 13.3. A mensagem do serviço vai à
 * tela verbatim.
 */
export function vincularPessoaAEmpresa(
  pessoaId: string,
  empresaId: string,
  { exigirSolta = false }: { exigirSolta?: boolean } = {},
): Promise<VinculoComEmpresa> {
  return chamar<VinculoComEmpresa>('cadastro', `/pessoas/${encodeURIComponent(pessoaId)}/empresa`, {
    method: 'POST',
    corpo: {
      empresa_id: empresaId,
      ...(exigirSolta ? { exigir_solta: true } : {}),
    } satisfies Omit<Esquemas['CorpoDoVinculoComEmpresa'], 'exigir_solta'> &
      Partial<Pick<Esquemas['CorpoDoVinculoComEmpresa'], 'exigir_solta'>>,
  })
}

/**
 * Vincula e relê os lotes de Pessoas — o nome e a Empresa do contato mudaram para quem os lê.
 *
 * O prefixo `['cadastro', 'pessoas']` alcança todo lote de `chaveDoLoteDePessoas`, inclusive o da
 * tela de Conversa, que resolve o contato por ele. Desde a 13.3 a lista de Pessoas da Empresa **de
 * destino** também é relida (`chaveDasPessoas`): ela ganhou um contato, e a ficha aberta noutra
 * tela mostraria a lista velha até a próxima navegação.
 */
export function useVincularPessoaAEmpresa() {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: ({
      pessoaId,
      empresaId,
      exigirSolta = false,
    }: {
      pessoaId: string
      empresaId: string
      exigirSolta?: boolean
    }) => vincularPessoaAEmpresa(pessoaId, empresaId, { exigirSolta }),
    onSuccess: (_vinculo, { empresaId }) =>
      Promise.all([
        clienteDeConsultas.invalidateQueries({ queryKey: ['cadastro', 'pessoas'] }),
        clienteDeConsultas.invalidateQueries({ queryKey: chaveDasPessoas(empresaId) }),
      ]),
  })
}

/**
 * Edita a Pessoa **solta** (`200`) — `PUT /pessoas/{pessoa_id}` (13.3, D-1).
 *
 * Até a 13.3 a única edição de Pessoa exigia Empresa no caminho, e a solta — a que nasceu com o
 * número por nome quando escreveu pelo WhatsApp — não se corrigia em lugar nenhum. O corpo é o
 * mesmo `CorpoDeEdicaoDePessoa` dos quatro campos. **A vinculada não passa por aqui**: o serviço
 * responde `409` ("edite pela ficha da Empresa"), e quem a edita é `editarPessoa`, pela rota da
 * Empresa, onde a RLS da ficha vale e o ERP fica sabendo. Nenhum evento sai desta rota.
 *
 * Exige `editar_empresa`; `404` quando a Pessoa não existe ou a RLS a esconde (a solta é de quem a
 * recebeu e da gestão).
 *
 * **Desde a 13.17 é a rota do painel Dados do contato, e o telefone não se troca por ela**: o
 * corpo leva o telefone gravado (ou, na Pessoa sem telefone, o número da conversa). Vazio é `422`
 * `telefone_obrigatorio`; outra chave, `422` `telefone_da_conversa`. O aceite do telefone de outra
 * Pessoa da 13.14a saiu com a troca.
 */
export function editarPessoaSolta(pessoaId: string, pessoa: EdicaoDePessoa): Promise<Pessoa> {
  return chamar<Pessoa>('cadastro', `/pessoas/${encodeURIComponent(pessoaId)}`, {
    method: 'PUT',
    corpo: corpoDePessoa(pessoa),
  })
}

/** Edita a Pessoa solta e relê os lotes de Pessoas — a lista de conversas mostra o nome novo. */
export function useEditarPessoaSolta() {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: ({ pessoaId, pessoa }: { pessoaId: string; pessoa: EdicaoDePessoa }) =>
      editarPessoaSolta(pessoaId, pessoa),
    onSuccess: () => clienteDeConsultas.invalidateQueries({ queryKey: ['cadastro', 'pessoas'] }),
  })
}

/* --- A tela Pessoas (13.4, P13) ------------------------------------------------------ */

/**
 * Uma linha da tela Pessoas: nome, cargo, telefone e as Empresas visíveis (13.19c2: `empresas`, na
 * ordem do vínculo; vazia na Pessoa solta).
 */
export type PessoaNaLista = Esquemas['RespostaDePessoaNaLista']

/** Uma Empresa visível da Pessoa na tela Pessoas: o link e o "+N" (13.19c2). */
export type EmpresaDaPessoaNaLista = Esquemas['EmpresaDaPessoaNaLista']

/** A página da tela Pessoas e o total do mesmo filtro, os dois pós-RLS. */
export type PaginaDePessoas = Esquemas['RespostaDaListaDePessoas']

/** O que a tela Pessoas pede: o termo, a página e o tamanho dela. */
export interface ParametrosDaListaDePessoas {
  /** Com letra, o nome (sem acento nem pontuação); sem letra e com dígito, o telefone. */
  busca?: string
  /** De 1 a `PAGINA_MAXIMA`. */
  pagina?: number
  /** De 1 a `LIMITE_MAXIMO`. */
  limite?: number
  /** As Pessoas com esta etiqueta da equipe (13.6). */
  etiquetaId?: string
}

function normalizarParametrosDePessoas({
  busca,
  pagina,
  limite,
  etiquetaId,
}: ParametrosDaListaDePessoas) {
  const termo = busca?.trim()
  return {
    busca: termo === undefined || termo === '' ? undefined : termo,
    pagina,
    limite,
    etiquetaId: etiquetaId === undefined || etiquetaId === '' ? undefined : etiquetaId,
  }
}

/** A chave de **uma página** da tela Pessoas — debaixo do prefixo dos lotes, que a invalida. */
export const chaveDaListaDePessoas = (parametros: ParametrosDaListaDePessoas = {}) =>
  [...CHAVE_DOS_LOTES_DE_PESSOAS, 'lista', normalizarParametrosDePessoas(parametros)] as const

/**
 * Lê uma página da tela Pessoas (`200`) — `GET /pessoas/lista` (13.4).
 *
 * Rota própria, e não o lote por `ids`: aquele continua exigindo `ids`. Quem recorta é a RLS — o
 * Vendedor vê as Pessoas das Empresas que enxerga e as soltas que recebeu. O termo vai como foi
 * digitado: é o serviço que decide se é nome ou telefone, pela mesma chave da resolução do WhatsApp.
 */
export function listarPessoasDaBase(
  parametros: ParametrosDaListaDePessoas = {},
): Promise<PaginaDePessoas> {
  const { busca, pagina, limite, etiquetaId } = normalizarParametrosDePessoas(parametros)
  const consulta = new URLSearchParams()
  if (busca !== undefined) {
    consulta.set('busca', busca)
  }
  if (pagina !== undefined) {
    consulta.set('pagina', String(pagina))
  }
  if (limite !== undefined) {
    consulta.set('limite', String(limite))
  }
  if (etiquetaId !== undefined) {
    consulta.set('etiqueta_id', etiquetaId)
  }
  const texto = consulta.toString()
  return chamar<PaginaDePessoas>('cadastro', `/pessoas/lista${texto === '' ? '' : `?${texto}`}`)
}

/** Uma página da tela Pessoas, com a anterior na tela enquanto a próxima chega (como Empresas). */
export function usePessoasDaBase(
  parametros: ParametrosDaListaDePessoas = {},
  { habilitada = true }: { habilitada?: boolean } = {},
) {
  return useQuery({
    queryKey: chaveDaListaDePessoas(parametros),
    queryFn: () => listarPessoasDaBase(parametros),
    retry: false,
    placeholderData: keepPreviousData,
    // 13.18: a etiqueta do filtro fora do catálogo de Pessoas não vai ao serviço.
    enabled: habilitada,
  })
}

/** Uma sugestão do Nome da ficha nova (13.19c1): nome, cargo, telefone e as Empresas pela base. */
export type PessoaSugerida = Esquemas['PessoaSugerida']

/** Uma Empresa da Pessoa sugerida — a primeira é a que o aviso nomeia. */
export type EmpresaDaPessoaSugerida = Esquemas['EmpresaDaPessoaSugerida']

/** Uma página da sugestão de Pessoas e o total do mesmo filtro. */
export type PaginaDeSugestoesDePessoas = Esquemas['RespostaDaSugestaoDePessoas']

/** Quantas sugestões de Pessoa cada página traz — o `LIMITE_DA_SUGESTAO_DE_PESSOAS` do serviço. */
export const SUGESTOES_DE_PESSOAS_POR_PAGINA = 10

/**
 * Lê uma página da sugestão de Pessoas (`200`) — `GET /pessoas/sugestoes` (13.19c1).
 *
 * As Pessoas que quem cadastra vê **pela base** (sem o Grupo, a PA-1 e a PA-3: é o serviço que
 * recorta), pelo nome ou pelo telefone; `busca` é obrigatória. Exige `editar_empresa`.
 */
export function listarSugestoesDePessoas(
  busca: string,
  pagina = 1,
): Promise<PaginaDeSugestoesDePessoas> {
  const consulta = new URLSearchParams({ busca, pagina: String(pagina) })
  return chamar<PaginaDeSugestoesDePessoas>('cadastro', `/pessoas/sugestoes?${consulta.toString()}`)
}

/** A página que não tem a forma do contrato (um encenador, o serviço anterior) é falha, e não vazia. */
function conferirAPaginaDeSugestoes(pagina: unknown): PaginaDeSugestoesDePessoas {
  const candidata = pagina as Partial<PaginaDeSugestoesDePessoas> | null
  if (
    candidata === null ||
    typeof candidata !== 'object' ||
    !Array.isArray(candidata.itens) ||
    typeof candidata.total !== 'number'
  ) {
    throw new Error('a sugestão de Pessoas não respondeu com a página do contrato')
  }
  return candidata as PaginaDeSugestoesDePessoas
}

/**
 * As sugestões do Nome da ficha nova (13.19c1): de 10 em 10, no molde de `useSugestoesDeEmpresas`.
 *
 * Uma consulta infinita por termo, debaixo de `CHAVE_DOS_LOTES_DE_PESSOAS`: todo cadastro de Pessoa
 * relê as sugestões também. A página seguinte existe enquanto a última veio cheia e as carregadas
 * não chegaram ao `total`. A ordem é do servidor (a semelhança do nome).
 */
export function useSugestoesDePessoas(termo: string, { habilitada = true } = {}) {
  return useInfiniteQuery<
    PaginaDeSugestoesDePessoas,
    Error,
    InfiniteData<PaginaDeSugestoesDePessoas, number>,
    readonly unknown[],
    number
  >({
    queryKey: [...CHAVE_DOS_LOTES_DE_PESSOAS, 'sugestoes', termo],
    queryFn: async ({ pageParam }) =>
      conferirAPaginaDeSugestoes(await listarSugestoesDePessoas(termo, pageParam)),
    initialPageParam: 1,
    getNextPageParam: (ultima, paginas, ultimaPagina) => {
      const carregadas = paginas.reduce((soma, pagina) => soma + pagina.itens.length, 0)
      return ultima.itens.length === SUGESTOES_DE_PESSOAS_POR_PAGINA && carregadas < ultima.total
        ? ultimaPagina + 1
        : undefined
    },
    retry: false,
    placeholderData: keepPreviousData,
    enabled: habilitada,
  })
}

/**
 * O que `useNomesDePessoas` devolve: o mapa **e** o estado dos lotes que o produziram.
 *
 * O mapa sozinho não basta, pela mesma razão de `NomesDeEmpresas`: um `id` fora dele quer dizer
 * três coisas muito diferentes — o lote ainda está no ar, o lote falhou, ou a policy do `cadastro`
 * não devolveu aquela ficha. Só a terceira é a degradação documentada.
 */
export interface NomesDePessoas {
  /** `id -> nome`. O que a policy não devolveu simplesmente não está aqui. */
  nomes: ReadonlyMap<string, string>
  /** `id -> etiqueta_ids` (13.6): as etiquetas da equipe de cada Pessoa do lote. */
  etiquetas: ReadonlyMap<string, readonly string[]>
  /**
   * `id -> empresa_ids` (13.19c2): as Empresas de cada Pessoa do lote que quem olha enxerga, na
   * ordem do vínculo. A Pessoa que a policy não devolveu não está aqui — e quem lê o mapa distingue
   * "não veio" (`has` falso) de "veio solta" (a lista vazia).
   */
  empresas: ReadonlyMap<string, readonly string[]>
  /**
   * `id -> empresa_ids_editaveis` (13.19c2, code review C): as Empresas em que quem olha edita a
   * Pessoa e **cria Negócio** — as do "+ Negócio" da conversa. Pela mesma regra de `empresas`.
   */
  editaveis: ReadonlyMap<string, readonly string[]>
  /**
   * `id -> pode_editar` (13.20c, code review A4): se quem olha edita a Pessoa — falso para a lida
   * só pelo Negócio sem Empresa que recebeu. Só a Pessoa que veio com o campo entra (o `cadastro`
   * anterior não o manda): fora do mapa é "não sei".
   */
  podeEditar: ReadonlyMap<string, boolean>
  /**
   * Algum lote ainda está no ar — a ausência de um `id` no mapa ainda não quer dizer nada.
   *
   * **Ele é lido**, e a tela de Conversa o usa para o que ele existe: enquanto o lote não chega,
   * o nome de toda linha cai para o número, indistinguível da Pessoa solta (que também é o
   * número). `Conversas.tsx` transforma isso em `aria-busy` na lista, em vez de deixar quem usa
   * leitor de tela ler uma coluna de telefones como se fosse a resposta final.
   */
  carregando: boolean
  /** Algum lote falhou. O que falta não é sigilo: é a resposta que não chegou. */
  falhou: boolean
}

/**
 * Resolve o nome de um conjunto de Pessoas, num mapa `id -> nome`.
 *
 * **É a peça que o AD-25 exige na tela de Conversa (8.3).** O `comunicacao` entrega `pessoa_id`, e
 * o nome do contato é do `cadastro`: resolvê-lo lá, com token de serviço, entregaria à tela um nome
 * que a policy **deste** serviço esconde daquele usuário — os dois pisos são independentes de
 * propósito. Com o SPA resolvendo pelo JWT de quem está olhando, cada serviço continua dono do seu.
 *
 * `useQueries` sobre **lotes**, e não uma consulta por id: uma lista de conversas com dezenas de
 * linhas seriam dezenas de idas à rede. É o mesmo molde de `useNomesDeEmpresas`.
 *
 * `retry: false` porque a recusa esperada não muda por insistir, e `staleTime` alto porque nome de
 * contato quase não muda: reconsultar a cada montagem gastaria uma ida à rede por abertura de tela.
 */
export function useNomesDePessoas(ids: readonly string[]): NomesDePessoas {
  const lotes = useMemo(() => {
    // Ordenado e sem repetição, pela mesma razão de `useNomesDeEmpresas`: a chave de cache é
    // derivada do lote, e duas telas com os mesmos ids em ordens diferentes devem cair na mesma
    // entrada.
    const unicos = [...new Set(ids)].sort()
    const partes: string[][] = []
    for (let inicio = 0; inicio < unicos.length; inicio += TAMANHO_DO_LOTE) {
      partes.push(unicos.slice(inicio, inicio + TAMANHO_DO_LOTE))
    }
    return partes
  }, [ids])

  return useQueries({
    queries: lotes.map((lote) => ({
      queryKey: chaveDoLoteDePessoas(lote),
      queryFn: () => lerPessoasPorIds(lote),
      retry: false,
      staleTime: 5 * 60 * 1000,
    })),
    combine: combinarOsLotesDePessoas,
  })
}

/**
 * O `combine` de `useNomesDePessoas` — **no módulo**, e não uma seta dentro do componente.
 *
 * A identidade estável é o ponto, e ela é de desempenho: o `useQueries` só reaproveita o resultado
 * anterior enquanto a função `combine` for a **mesma**; declarada em linha, ela nasce nova a cada
 * renderização e devolve um `Map` novo, que nenhuma comparação estrutural consegue reaproveitar. É
 * a mesma armadilha, e a mesma correção, de `combinarOsLotes`.
 */
function combinarOsLotesDePessoas(
  resultados: readonly { data?: Pessoa[]; isPending: boolean; isError: boolean }[],
): NomesDePessoas {
  return {
    nomes: new Map(
      resultados
        .flatMap((resultado) => resultado.data ?? [])
        .map((pessoa) => [pessoa.id, pessoa.nome] as const),
    ),
    etiquetas: new Map(
      resultados
        .flatMap((resultado) => resultado.data ?? [])
        .map((pessoa) => [pessoa.id, etiquetaIdsDe(pessoa)] as const),
    ),
    /* Só a Pessoa que veio com a lista entra: sem ela (um `cadastro` anterior à 13.19c2), quem lê
       o mapa cai no que já tem (o `empresa_id` da conversa), em vez de tomar `undefined` por lista. */
    empresas: new Map(
      resultados
        .flatMap((resultado) => resultado.data ?? [])
        .flatMap((pessoa) => {
          const empresas = empresaIdsDe(pessoa)
          return empresas === undefined ? [] : [[pessoa.id, empresas] as const]
        }),
    ),
    editaveis: new Map(
      resultados
        .flatMap((resultado) => resultado.data ?? [])
        .flatMap((pessoa) =>
          empresaIdsDe(pessoa) === undefined
            ? []
            : [[pessoa.id, empresaIdsEditaveisDe(pessoa)] as const],
        ),
    ),
    podeEditar: new Map(
      resultados
        .flatMap((resultado) => resultado.data ?? [])
        .flatMap((pessoa) => {
          const pode = (pessoa as Partial<Pessoa>).pode_editar
          return pode === undefined ? [] : [[pessoa.id, pode] as const]
        }),
    ),
    carregando: resultados.some((resultado) => resultado.isPending),
    falhou: resultados.some((resultado) => resultado.isError),
  }
}

/* --- As cinco listas de domínio: administração (história 2.3) --------------------- */

/**
 * Um valor de lista de domínio como a **administração** o vê: com `tipo` e `ativo`.
 *
 * É outra forma que `OpcaoDeDominio`, que é a do vocabulário: o seletor da ficha da Empresa
 * recebe só as ativas de uma lista que ele já sabe qual é, e `ativo` é justamente o campo que
 * a tela de Administração precisa — é ele que distingue "excluída" de "sumiu".
 */
export type OpcaoAdministrada = Esquemas['RespostaDeOpcaoAdministrada']

/** Uma das cinco listas. Enum fechado no serviço: lista nova exige deploy, não um `POST`. */
export type TipoDeLista = Esquemas['TipoDeLista']

/** O que a tela envia para cadastrar: em qual lista, e com que rótulo. */
export type NovaOpcao = Esquemas['CorpoDeNovaOpcao']

/** O que a tela envia para editar: o rótulo, e só. Mover de lista não é editar. */
export type EdicaoDeOpcao = Esquemas['CorpoDeEdicaoDeOpcao']

function caminhoDaOpcao(opcaoId: string): string {
  return `/opcoes-de-dominio/${encodeURIComponent(opcaoId)}`
}

/** Prefixo comum das chaves por `id` — é por ele que a invalidação alcança todas de uma vez. */
export const CHAVE_DA_OPCAO = ['cadastro', 'opcao-de-dominio'] as const

/** Chave de cache de **um** valor de lista, resolvido pelo `id`. */
export const chaveDaOpcao = (opcaoId: string) => [...CHAVE_DA_OPCAO, opcaoId] as const

/**
 * Lê **um** valor de lista pelo `id` (`200`), ativo ou não.
 *
 * É a outra metade do FR-67, e o par dela é `lerOpcoesDeDominio`: aquela traz o vocabulário
 * das **escolhas novas** (só as ativas), esta traz o rótulo de um valor que já está gravado
 * num registro — inclusive um que a gestão excluiu. O AC do épico manda o registro antigo
 * continuar **exibindo** o valor, como o FR-69 promete para o Tipo de Tarefa; sem esta rota a
 * ficha teria o `id` e não teria o rótulo.
 *
 * Só autenticada: vocabulário é público. `404` quando o `id` não existe.
 */
export function lerOpcao(opcaoId: string): Promise<OpcaoAdministrada> {
  return chamar<OpcaoAdministrada>('cadastro', caminhoDaOpcao(opcaoId))
}

/**
 * Resolve o rótulo dos `ids` pedidos, num mapa `id -> rotulo`.
 *
 * `useQueries` porque a quantidade varia com a ficha: são os poucos ids que ela carrega e que
 * **não** estão no vocabulário ativo. Um `useQuery` por campo seria quatro consultas fixas,
 * três delas para ids que a lista já sabe rotular.
 *
 * `retry: false` pela mesma razão de `useEmpresa`: a recusa esperada é o `404` de um id que
 * não existe mesmo, e insistir não o faz aparecer. O que não resolver simplesmente não entra
 * no mapa, e quem chamou cai no rótulo genérico — degradar é melhor que travar a ficha.
 *
 * `staleTime` alto: rótulo de lista de domínio quase nunca muda, e reconsultar a cada
 * montagem gastaria uma ida à rede por campo, por abertura de ficha. Quem edita é a tela da
 * Administração, e ela invalida **também** este prefixo — sem isso, renomear um valor já
 * excluído (caminho que `editar_opcao` aceita de propósito) deixaria a ficha exibindo o
 * rótulo velho por até cinco minutos.
 */
export function useRotulosDeOpcoes(ids: readonly string[]): ReadonlyMap<string, string> {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: chaveDaOpcao(id),
      queryFn: () => lerOpcao(id),
      retry: false,
      staleTime: 5 * 60 * 1000,
    })),
    combine: (resultados) =>
      new Map(
        resultados
          .map((resultado) => resultado.data)
          .filter((opcao): opcao is OpcaoAdministrada => opcao !== undefined)
          .map((opcao) => [opcao.id, opcao.rotulo] as const),
      ),
  })
}

/**
 * O desfecho de `cadastrarOpcao`: a opção, e **como** ela veio a existir.
 *
 * `restaurada` é `true` quando o rótulo existia excluído e o serviço o reativou — mesmo `id`,
 * e portanto tudo o que já apontava para ele continua apontando. Nada nasceu, e dizer
 * "cadastrado" ali seria contar a história errada de um ato que a trilha registra como
 * `opcao_de_dominio_reativada`.
 */
export interface OpcaoCadastrada {
  opcao: OpcaoAdministrada
  restaurada: boolean
}

/**
 * Cadastra o valor (`201`) — **ou reativa** o que existia excluído (`200`, mesmo `id`).
 *
 * Exige perfil de gestão (Admin ou Gestor comercial); os demais recebem `403`. `409` quando o
 * rótulo já existe **ativo** naquela lista, `422` quando ele vem vazio ou longo demais.
 *
 * O status é lido, e não descartado: é ele — e só ele — que separa os dois desfechos, porque o
 * corpo dos dois é idêntico. O serviço declara os dois no schema, então isto não é adivinhação
 * sobre um comportamento não documentado.
 *
 * Os campos vão **um a um**, e não o objeto que chegou: o corpo do serviço é fechado
 * (`extra="forbid"`), e um objeto de formulário com um campo a mais viraria um `422` genérico
 * que não diz qual campo sobrou.
 */
export async function cadastrarOpcao(opcao: NovaOpcao): Promise<OpcaoCadastrada> {
  const { corpo, status } = await chamarComStatus<OpcaoAdministrada>(
    'cadastro',
    '/opcoes-de-dominio',
    { method: 'POST', corpo: { tipo: opcao.tipo, rotulo: opcao.rotulo } },
  )
  return { opcao: corpo, restaurada: status === 200 }
}

/**
 * Troca o rótulo do valor (`200`). Exige perfil de gestão.
 *
 * Os registros que apontam para ele passam a exibir o rótulo novo sozinhos: a referência é por
 * `uuid`, e o texto mora numa linha só. **Reenviar o rótulo que já está gravado não publica
 * evento nem deixa registro na trilha** — o serviço compara antes de gravar.
 */
export function editarOpcao(opcaoId: string, opcao: EdicaoDeOpcao): Promise<OpcaoAdministrada> {
  return chamar<OpcaoAdministrada>('cadastro', caminhoDaOpcao(opcaoId), {
    method: 'PUT',
    corpo: { rotulo: opcao.rotulo },
  })
}

/**
 * Exclui o valor (`200` com `ativo: false`). Exige perfil de gestão.
 *
 * A exclusão é **lógica** (FR-67): o valor some das escolhas novas e continua sendo exibido
 * nos registros antigos — a Empresa que aponta para ele mantém o mesmo `categoria_id`. Por
 * isso a resposta tem corpo, e não é um `204`: o corpo é a prova de que a linha continua lá.
 */
export function excluirOpcao(opcaoId: string): Promise<OpcaoAdministrada> {
  return chamar<OpcaoAdministrada>('cadastro', caminhoDaOpcao(opcaoId), { method: 'DELETE' })
}

/**
 * As três mutações compartilham o mesmo desfecho: **reler o vocabulário do serviço**.
 *
 * Invalidar, e não remendar o cache com a resposta, é o ponto: `CHAVE_DAS_OPCOES` é a mesma
 * que alimenta os seletores da ficha da Empresa, e a resposta de uma mutação é **uma** opção,
 * não as cinco listas. Um `setQueryData` com ela apagaria o resto do vocabulário da tela.
 *
 * São **duas** chaves, e não uma: `CHAVE_DA_OPCAO` não é prefixo de `CHAVE_DAS_OPCOES`, e a
 * ficha da Empresa lê por ela o rótulo do valor excluído que ainda referencia. Invalidar só o
 * vocabulário deixaria esse rótulo velho no cache até o `staleTime` vencer.
 */
function useInvalidarAsOpcoes() {
  const clienteDeConsultas = useQueryClient()
  // A promessa das duas continua sendo devolvida: `onSuccess` a aguarda, e é isso que mantém
  // a mutação "pendente" até a lista da tela já refletir o ato.
  return () =>
    Promise.all([
      clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DAS_OPCOES }),
      clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DA_OPCAO }),
    ])
}

/** Cadastra (ou reativa) o valor e relê o vocabulário. */
export function useCadastrarOpcao() {
  const aoTerminar = useInvalidarAsOpcoes()
  return useMutation({
    mutationFn: (opcao: NovaOpcao) => cadastrarOpcao(opcao),
    onSuccess: aoTerminar,
  })
}

/** Grava o rótulo novo e relê o vocabulário. */
export function useEditarOpcao() {
  const aoTerminar = useInvalidarAsOpcoes()
  return useMutation({
    mutationFn: ({ opcaoId, opcao }: { opcaoId: string; opcao: EdicaoDeOpcao }) =>
      editarOpcao(opcaoId, opcao),
    onSuccess: aoTerminar,
  })
}

/** Exclui logicamente o valor e relê o vocabulário — ele some das escolhas novas. */
export function useExcluirOpcao() {
  const aoTerminar = useInvalidarAsOpcoes()
  return useMutation({
    mutationFn: (opcaoId: string) => excluirOpcao(opcaoId),
    onSuccess: aoTerminar,
  })
}

/* --- Os campos personalizados de Empresa (FR-79, 11.3) --------------------------------------- */

/** A administração dos campos personalizados de Empresa — a forma é a mesma do `negocios`. */
export const camposDaEmpresa = criarApiDeCamposPersonalizados('cadastro')

/**
 * O nome das definições de campo personalizado que a ficha ainda carrega e que o catálogo ativo
 * **não** traz — as desativadas (FR-67: o valor gravado continua legível). `GET
 * /campos-personalizados/{id}` devolve ativa ou não; o que não vier cai no nome genérico da tela.
 * Uma consulta por id, e ids que tendem a zero: a tela só pergunta pelos que faltam.
 */
export function useNomesDeCamposDesativados(ids: readonly string[]): ReadonlyMap<string, string> {
  const consultas = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['cadastro', 'campo-personalizado', id] as const,
      queryFn: () =>
        chamar<{ id: string; nome: string }>(
          'cadastro',
          `/campos-personalizados/${encodeURIComponent(id)}`,
        ),
      retry: false,
      staleTime: Infinity,
    })),
  })
  return new Map(
    consultas.flatMap((consulta, indice) => {
      const id = ids[indice]
      return consulta.data && id !== undefined ? [[id, consulta.data.nome] as const] : []
    }),
  )
}

/* --- As etiquetas da equipe (13.6, FR-86, FR-62/PN4) ---------------------------------------- */

/** Uma etiqueta do catálogo: nome, cor da paleta pastel fechada (o nome da cor) e tipo (13.18). */
export type Etiqueta = Esquemas['RespostaDeEtiqueta']

/**
 * De que catálogo a etiqueta é (13.18): Empresas e Pessoas têm cada uma o seu, e o tipo não muda.
 * "VIP" de Empresa e "VIP" de Pessoa são duas.
 */
export type TipoDeEtiqueta = Esquemas['TipoDeEtiqueta']

/** O corpo de editar — completo (D-1), **sem** o tipo, que é imutável. */
export type CorpoDeEtiqueta = Esquemas['CorpoDeEtiqueta']

/** O corpo de criar: nome, cor e o tipo do catálogo em que ela nasce (13.18). */
export type CorpoDeNovaEtiqueta = Esquemas['CorpoDeNovaEtiqueta']

/** A cor de uma etiqueta: os oito nomes do `DESIGN.md`. */
export type CorDaEtiqueta = Esquemas['CorPastel']

/**
 * O número do `409` de apagar em uso (13.18): o tipo e o total, inclusive o que quem olha não
 * enxerga.
 */
export type UsoDaEtiqueta = Esquemas['UsoDaEtiquetaNoEnvelope']

/** Onde a etiqueta se aplica: uma Empresa ou uma Pessoa — e o tipo do alvo é o do catálogo. */
export interface AlvoDaEtiqueta {
  tipo: TipoDeEtiqueta
  id: string
}

/** O código do `409` de etiqueta em uso — é ele que diz que `extras.uso` está lá. */
export const CODIGO_DA_ETIQUETA_EM_USO = 'etiqueta_em_uso'

/** O prefixo dos dois catálogos — invalidá-lo relê os dois. */
export const CHAVE_DAS_ETIQUETAS = ['cadastro', 'etiquetas'] as const

/** A chave de um catálogo: o tipo entra nela (13.18), e as telas de cada tipo não se misturam. */
export const chaveDasEtiquetas = (tipo: TipoDeEtiqueta) => [...CHAVE_DAS_ETIQUETAS, tipo] as const

/** O teto do nome — o mesmo `TAMANHO_DO_NOME_DA_ETIQUETA` do serviço (`maxLength` do contrato). */
export const TAMANHO_DO_NOME_DA_ETIQUETA = 40

/** Quantas etiquetas a ficha nova leva (13.18) — o `maxItems` de `etiqueta_ids` no serviço. */
export const LIMITE_DE_ETIQUETAS_NA_FICHA_NOVA = 20

/**
 * Os `etiqueta_ids` de uma leitura, ou `[]` quando o campo não veio.
 *
 * O tipo gerado diz que ele sempre vem; a janela do deploy (SPA novo sobre o backend velho) diz que
 * não — como `foiAdotada` faz com `adotada`.
 */
export function etiquetaIdsDe(leitura: { etiqueta_ids?: readonly string[] }): readonly string[] {
  return leitura.etiqueta_ids ?? []
}

function caminhoDaEtiqueta(etiquetaId: string): string {
  return `/etiquetas/${encodeURIComponent(etiquetaId)}`
}

function caminhoDoVinculo(alvo: AlvoDaEtiqueta, etiquetaId: string): string {
  const recurso = alvo.tipo === 'empresa' ? 'empresas' : 'pessoas'
  return `/${recurso}/${encodeURIComponent(alvo.id)}${caminhoDaEtiqueta(etiquetaId)}`
}

/** O catálogo de um tipo, por nome (`200`). Gerenciar e ler são de todo usuário (FR-86). */
export function listarEtiquetas(tipo: TipoDeEtiqueta): Promise<Etiqueta[]> {
  return chamar<Etiqueta[]>('cadastro', `/etiquetas?tipo=${encodeURIComponent(tipo)}`)
}

/**
 * Cria (`201`) no catálogo do tipo; `409` com o nome repetido **no tipo**, `422` com nome sem letra
 * nem número.
 */
export function criarEtiqueta(corpo: CorpoDeNovaEtiqueta): Promise<Etiqueta> {
  return chamar<Etiqueta>('cadastro', '/etiquetas', {
    method: 'POST',
    corpo: { nome: corpo.nome, cor: corpo.cor, tipo: corpo.tipo },
  })
}

/** Renomeia e/ou troca a cor (`200`) — vale em todo lugar na próxima leitura do catálogo. */
export function editarEtiqueta(etiquetaId: string, corpo: CorpoDeEtiqueta): Promise<Etiqueta> {
  return chamar<Etiqueta>('cadastro', caminhoDaEtiqueta(etiquetaId), {
    method: 'PUT',
    corpo: { nome: corpo.nome, cor: corpo.cor },
  })
}

/** Apaga a etiqueta sem uso (`204`); em uso, `409` com `extras.uso` (veja `usoDaEtiquetaEmUso`). */
export async function apagarEtiqueta(etiquetaId: string): Promise<void> {
  await chamarComStatus<undefined>('cadastro', caminhoDaEtiqueta(etiquetaId), { method: 'DELETE' })
}

/** Aplica (idempotente) e devolve os `etiqueta_ids` do alvo. Exige `editar_empresa`. */
export async function aplicarEtiqueta(
  alvo: AlvoDaEtiqueta,
  etiquetaId: string,
): Promise<readonly string[]> {
  const corpo = await chamar<Esquemas['RespostaDasEtiquetasDoAlvo']>(
    'cadastro',
    caminhoDoVinculo(alvo, etiquetaId),
    { method: 'PUT' },
  )
  return corpo.etiqueta_ids
}

/** Retira (idempotente) e devolve os `etiqueta_ids` do alvo. Exige `editar_empresa`. */
export async function retirarEtiqueta(
  alvo: AlvoDaEtiqueta,
  etiquetaId: string,
): Promise<readonly string[]> {
  const corpo = await chamar<Esquemas['RespostaDasEtiquetasDoAlvo']>(
    'cadastro',
    caminhoDoVinculo(alvo, etiquetaId),
    { method: 'DELETE' },
  )
  return corpo.etiqueta_ids
}

/**
 * O tipo e o número da recusa de apagar em uso (13.18), ou `undefined` quando a falha é outra.
 *
 * A mensagem do `409` é do servidor e vai verbatim para a tela; o tipo e o número servem só para
 * decidir entre "Ver empresas" e "Ver pessoas" e para o aviso das linhas escondidas.
 */
export function usoDaEtiquetaEmUso(falha: unknown): UsoDaEtiqueta | undefined {
  if (!(falha instanceof ErroDaApi) || falha.codigo !== CODIGO_DA_ETIQUETA_EM_USO) {
    return undefined
  }
  const uso = falha.extras.uso as { tipo?: unknown; quantidade?: unknown } | undefined
  if ((uso?.tipo !== 'empresa' && uso?.tipo !== 'pessoa') || typeof uso.quantidade !== 'number') {
    return undefined
  }
  return { tipo: uso.tipo, quantidade: uso.quantidade }
}

/**
 * O catálogo de um tipo, para a tela inteira — uma consulta por tipo, e não uma por chip.
 *
 * `staleTime` de 30 s: renomear numa aba invalida esta chave na mesma aba; a outra aba vê o nome
 * novo na próxima leitura (o `refetchOnWindowFocus` de sempre, ou depois dos 30 s).
 */
export function useEtiquetas(tipo: TipoDeEtiqueta) {
  return useQuery({
    queryKey: chaveDasEtiquetas(tipo),
    queryFn: () => listarEtiquetas(tipo),
    retry: false,
    staleTime: 30 * 1000,
    /*
     * O catálogo é lido por toda ficha, lista e conversa: uma resposta que não seja lista (o
     * gateway de uma versão sem a rota, um proxy que devolve `{}`) vira catálogo vazio — sem chip —,
     * e não derruba a tela inteira num `.map` de objeto.
     */
    select: (dados: unknown): Etiqueta[] => (Array.isArray(dados) ? (dados as Etiqueta[]) : []),
  })
}

function useInvalidarOCatalogo() {
  const clienteDeConsultas = useQueryClient()
  return () => clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DAS_ETIQUETAS })
}

/**
 * Toda mutação do catálogo relê o catálogo — **também na recusa**: o `409` de nome repetido e o
 * `404` de etiqueta apagada são sinais de que o catálogo desta aba envelheceu.
 */
export function useCriarEtiqueta() {
  const relerOCatalogo = useInvalidarOCatalogo()
  return useMutation({
    mutationFn: criarEtiqueta,
    onSettled: () => relerOCatalogo(),
  })
}

export function useEditarEtiqueta() {
  const relerOCatalogo = useInvalidarOCatalogo()
  return useMutation({
    mutationFn: ({ etiquetaId, corpo }: { etiquetaId: string; corpo: CorpoDeEtiqueta }) =>
      editarEtiqueta(etiquetaId, corpo),
    onSettled: () => relerOCatalogo(),
  })
}

export function useApagarEtiqueta() {
  const relerOCatalogo = useInvalidarOCatalogo()
  return useMutation({
    mutationFn: apagarEtiqueta,
    onSettled: () => relerOCatalogo(),
  })
}

/**
 * Aplicar e retirar relêem o que mostra as etiquetas do alvo: a ficha, as listas de Empresas (e os
 * lotes da conversa, que moram debaixo do mesmo prefixo) e as Pessoas (a tela Pessoas, o painel e
 * os lotes). Só a **recusa** relê também o catálogo (o `404` da etiqueta apagada no meio).
 *
 * As releituras **não** são esperadas: o `onSettled` não as devolve, e os controles destravam assim
 * que o serviço responde — o chip já vem da resposta (`EtiquetasDoAlvo`).
 */
function useInvalidarDepoisDoVinculo() {
  const clienteDeConsultas = useQueryClient()
  return (alvo: AlvoDaEtiqueta, falhou: boolean) => {
    if (alvo.tipo === 'empresa') {
      void clienteDeConsultas.invalidateQueries({ queryKey: chaveDaEmpresa(alvo.id) })
    }
    void clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DA_LISTA_DE_EMPRESAS })
    void clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_LOTES_DE_PESSOAS })
    if (falhou) {
      void clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DAS_ETIQUETAS })
    }
  }
}

export function useAplicarEtiqueta(alvo: AlvoDaEtiqueta) {
  const reler = useInvalidarDepoisDoVinculo()
  return useMutation({
    mutationFn: (etiquetaId: string) => aplicarEtiqueta(alvo, etiquetaId),
    onSettled: (_dados, falha) => {
      reler(alvo, falha !== null)
    },
  })
}

export function useRetirarEtiqueta(alvo: AlvoDaEtiqueta) {
  const reler = useInvalidarDepoisDoVinculo()
  return useMutation({
    mutationFn: (etiquetaId: string) => retirarEtiqueta(alvo, etiquetaId),
    onSettled: (_dados, falha) => {
      reler(alvo, falha !== null)
    },
  })
}

/* --- O Grupo de Empresas (13.9, FR-88) ---------------------------------------------------- */

/** O Grupo e as filiais que quem olha enxerga — e só elas (a RLS recorta; nada sobre as outras). */
export type Grupo = Esquemas['RespostaDeGrupo']

/** Uma filial do Grupo: o nome, o documento e o Responsável (o nome dele vem da `identidade`). */
export type FilialDoGrupo = Esquemas['RespostaDaFilial']

/** O teto do nome do Grupo, em pontos de código depois de NFC (o mesmo do serviço). */
export const TAMANHO_DO_NOME_DO_GRUPO = 120

/** O prefixo de cache dos Grupos — as mutações de Empresa o invalidam inteiro. */
export const CHAVE_DOS_GRUPOS = ['cadastro', 'grupo'] as const

export const chaveDoGrupo = (id: string) => [...CHAVE_DOS_GRUPOS, id] as const

function caminhoDoGrupo(grupoId: string): string {
  return `/grupos/${encodeURIComponent(grupoId)}`
}

/**
 * Lê o Grupo (`200`). `404` — com a mensagem do serviço — quando ele não existe **ou** quando quem
 * olha não é da gestão nem Responsável de uma filial dele (FR-88): as duas respostas são iguais.
 */
export function lerGrupo(id: string): Promise<Grupo> {
  return chamar<Grupo>('cadastro', caminhoDoGrupo(id))
}

/** Monta o Grupo com a primeira filial (`201`); `409` se ela não é sua ou já está num Grupo. */
export function criarGrupo(nome: string, empresaId: string): Promise<Grupo> {
  return chamar<Grupo>('cadastro', '/grupos', {
    method: 'POST',
    corpo: { nome, empresa_id: empresaId } satisfies Esquemas['CorpoDeNovoGrupo'],
  })
}

/** Renomeia (`200`). */
export function renomearGrupo(id: string, nome: string): Promise<Grupo> {
  return chamar<Grupo>('cadastro', caminhoDoGrupo(id), {
    method: 'PUT',
    corpo: { nome } satisfies Esquemas['CorpoDoNomeDoGrupo'],
  })
}

/** Inclui uma filial (`200`); `409` com a Empresa em outro Grupo. */
export function incluirFilial(grupoId: string, empresaId: string): Promise<Grupo> {
  return chamar<Grupo>(
    'cadastro',
    `${caminhoDoGrupo(grupoId)}/filiais/${encodeURIComponent(empresaId)}`,
    { method: 'PUT' },
  )
}

/** Retira uma filial (`204`) — a última desfaz o Grupo, e a resposta é a mesma. */
export function retirarFilial(grupoId: string, empresaId: string): Promise<void> {
  return chamar<void>(
    'cadastro',
    `${caminhoDoGrupo(grupoId)}/filiais/${encodeURIComponent(empresaId)}`,
    { method: 'DELETE' },
  )
}

/** Desfaz o Grupo (`204`): todas as filiais saem, inclusive as que quem desfaz não enxerga. */
export function desfazerGrupo(grupoId: string): Promise<void> {
  return chamar<void>('cadastro', caminhoDoGrupo(grupoId), { method: 'DELETE' })
}

/** A chave da lista dos Grupos (13.18) — debaixo do prefixo, que as mutações de Empresa invalidam. */
export const CHAVE_DA_LISTA_DE_GRUPOS = [...CHAVE_DOS_GRUPOS, 'lista'] as const

/**
 * Os Grupos que quem olha lê (`200`, 13.18): a gestão, todos; os outros, os Grupos de que são
 * Responsáveis de uma filial — cada um com as filiais que a pessoa enxerga. Sem paginação.
 */
export function listarGrupos(): Promise<Grupo[]> {
  return chamar<Grupo[]>('cadastro', '/grupos')
}

/**
 * A lista dos Grupos para o campo "Grupo" da ficha nova (13.18). Só lida com o campo à vista
 * (`habilitada`), e relida ao abrir: o Grupo montado agora há pouco em outra tela tem de aparecer.
 */
export function useGrupos(habilitada: boolean) {
  return useQuery({
    queryKey: CHAVE_DA_LISTA_DE_GRUPOS,
    queryFn: listarGrupos,
    enabled: habilitada,
    retry: false,
    refetchOnMount: 'always',
    select: (dados: unknown): Grupo[] => (Array.isArray(dados) ? (dados as Grupo[]) : []),
  })
}

/** O Grupo, com `retry: false`: o `404` esperado não muda por insistir. */
export function useGrupo(id: string | null | undefined) {
  return useQuery({
    queryKey: chaveDoGrupo(id ?? ''),
    queryFn: () => lerGrupo(id as string),
    enabled: id !== null && id !== undefined && id !== '',
    retry: false,
  })
}

/**
 * Relê o Grupo, as fichas das Empresas e a listagem depois de uma escrita de Grupo — e também
 * quando ela falha com `404` (o Grupo sumiu ou deixou de ser legível): a tela passa a mostrar o
 * que existe, em vez de insistir num Grupo que o serviço já não tem.
 */
function useReleituraDoGrupo(grupoId?: string) {
  const clienteDeConsultas = useQueryClient()
  return () =>
    Promise.all([
      clienteDeConsultas.invalidateQueries({
        queryKey: grupoId === undefined ? CHAVE_DOS_GRUPOS : chaveDoGrupo(grupoId),
      }),
      clienteDeConsultas.invalidateQueries({ queryKey: ['cadastro', 'empresa'] }),
      clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DA_LISTA_DE_EMPRESAS }),
    ])
}

function eNaoEncontrado(erro: unknown): boolean {
  return erro instanceof ErroDaApi && erro.status === 404
}

export function useCriarGrupo() {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: ({ nome, empresaId }: { nome: string; empresaId: string }) =>
      criarGrupo(nome, empresaId),
    //: O Grupo novo vem pronto na resposta; relê-se a ficha (o `grupo_id`) e a listagem, e não os
    //: outros Grupos em cache.
    onSuccess: (grupo: Grupo) => {
      clienteDeConsultas.setQueryData(chaveDoGrupo(grupo.id), grupo)
      return Promise.all([
        clienteDeConsultas.invalidateQueries({ queryKey: ['cadastro', 'empresa'] }),
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DA_LISTA_DE_EMPRESAS }),
      ])
    },
  })
}

export function useRenomearGrupo(grupoId: string) {
  const reler = useReleituraDoGrupo(grupoId)
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: (nome: string) => renomearGrupo(grupoId, nome),
    onSuccess: (grupo: Grupo) => {
      clienteDeConsultas.setQueryData(chaveDoGrupo(grupo.id), grupo)
    },
    onError: (erro) => (eNaoEncontrado(erro) ? reler() : undefined),
  })
}

export function useIncluirFilial(grupoId: string) {
  const reler = useReleituraDoGrupo(grupoId)
  return useMutation({
    mutationFn: (empresaId: string) => incluirFilial(grupoId, empresaId),
    onSuccess: () => reler(),
    onError: (erro) => (eNaoEncontrado(erro) ? reler() : undefined),
  })
}

/**
 * Retira a filial. **`saindo`** diz que quem pede deixa de ler o Grupo com ela (era a última que a
 * tela mostrava, ou a única de que ele é Responsável): o Grupo sai do cache em vez de ser relido —
 * relê-lo daria o `404` na tela que vai navegar.
 */
export function useRetirarFilial(grupoId: string) {
  const reler = useReleituraDoGrupo(grupoId)
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: ({ empresaId }: { empresaId: string; saindo: boolean }) =>
      retirarFilial(grupoId, empresaId),
    onSettled: (_resposta, erro, { saindo }) => {
      if (erro === null && saindo) {
        clienteDeConsultas.removeQueries({ queryKey: chaveDoGrupo(grupoId) })
        return Promise.all([
          clienteDeConsultas.invalidateQueries({ queryKey: ['cadastro', 'empresa'] }),
          clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DA_LISTA_DE_EMPRESAS }),
        ])
      }
      return reler()
    },
  })
}

/**
 * Desfaz o Grupo e **tira o Grupo do cache antes de reler o resto**: reler a chave dele daria o
 * `404` por um instante na tela que ainda está aberta, antes de ela navegar.
 */
export function useDesfazerGrupo(grupoId: string) {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: () => desfazerGrupo(grupoId),
    onSuccess: () => {
      clienteDeConsultas.removeQueries({ queryKey: chaveDoGrupo(grupoId) })
      return Promise.all([
        clienteDeConsultas.invalidateQueries({ queryKey: ['cadastro', 'empresa'] }),
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DA_LISTA_DE_EMPRESAS }),
      ])
    },
    onError: (erro) =>
      eNaoEncontrado(erro)
        ? clienteDeConsultas.invalidateQueries({ queryKey: chaveDoGrupo(grupoId) })
        : undefined,
  })
}
